import React from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  useRecordContext,
  Labeled,
  TextField,
  DateField,
  TopToolbar,
  Toolbar,
  SaveButton,
  DeleteButton,
  ListButton,
  Confirm,
  FormDataConsumer,
  useDataProvider,
  useNotify,
  useRefresh,
  useRedirect,
  useStore,
} from 'react-admin'
import {
  Grid,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button,
  TextField as MuiTextField,
  MenuItem,
  Stack,
  Autocomplete,
  Divider,
  InputAdornment,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

const UNIVERSAL_FIELDS = [
  'Supplier Name',
  'Signatory Details',
  'Contract Type',
  'Categorisation',
  'Linked Contracts',
  'Parent Agreement',
  'Referenced Schedules',
  'Effective Date',
  'Expiration Date',
  'Auto-Renewal Clause',
  'Notice Period',
  'Payment Terms',
  'Contract Value',
  'Liability Cap',
  'Indemnification',
  'Governing Law',
  'Dispute Resolution',
  'Termination Rights',
  'Change of Control',
  'Data Privacy',
  'Confidentiality',
  'Force Majeure',
  'Insurance',
  'Assignment Rights',
]

const NON_DOCUMENT_EVIDENCE_FIELDS = new Set(['Spend Taxonomy Source'])

const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return '#16a34a'
  if (confidence >= 50) return '#d97706'
  return '#dc2626'
}

const isFieldMandatory = (fieldName) => {
  if (!fieldName) return false
  const normalizedFieldName = fieldName.toLowerCase()
  return UNIVERSAL_FIELDS.some(
    (uf) =>
      normalizedFieldName.includes(uf.toLowerCase()) ||
      uf.toLowerCase().includes(normalizedFieldName)
  )
}

const hasUsableFieldValue = (value) => {
  if (value === null || value === undefined) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== 'None' && normalized !== 'null'
}

const getMetadataSortRank = (field) => {
  const isMandatory = isFieldMandatory(field?.field_name)
  const isFilled = hasUsableFieldValue(field?.field_value)
  if (isMandatory && isFilled) return 0
  if (isMandatory && !isFilled) return 1
  if (!isMandatory && isFilled) return 2
  return 3
}

const normalizeEvidenceSearchText = (value) => {
  if (!value) return ''
  return String(value).replace(/\s+/g, ' ').trim()
}

const normalizeForSearchComparison = (value) =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[\u2018\u2019\u201A\u2032\u2035`´]/g, "'")
    .replace(/[\u201C\u201D\u201E\u2033\u2036]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const stripLeadingListMarker = (value) =>
  String(value || '').replace(
    /^\s*(?:\(\s*\d+[a-z]?\s*\)|\d+[a-z]?[.)])\s*/i,
    ''
  )

const stripTrailingSearchPunctuation = (value) =>
  String(value || '')
    .replace(/\s*[;:]\s*$/g, '')
    .trim()

const SEARCH_LIST_DELIMITER_PATTERN = /(?:[;\n]+|…+|(?:\.\s*){3,})/
const LABEL_VALUE_QUERY_PATTERN =
  /(?:^|[;,])\s*(name(?:\s*\(printed\))?|job title|title)\s*:?\s*([^;,]+)/gi
const ISO_DATE_PATTERN = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/
const ROMAN_TIER_FRAGMENT_PATTERN =
  /^[a-z][a-z0-9&'/+-]*(?:\s+[a-z0-9&'/+-]+){0,4}\s+(?:i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i
const PERSON_NAME_FRAGMENT_PATTERN =
  /^[a-z][a-z'.-]*(?:\s+[a-z][a-z'.-]*){1,3}$/i
const PERSON_ROLE_PAREN_PATTERN =
  /^\s*([a-z][a-z'.-]*(?:\s+[a-z][a-z'.-]*){1,3})\s*\(([^)]+)\)\s*$/i
const PERSON_ROLE_TITLE_PATTERN =
  /^\s*([a-z][a-z'.-]*(?:\s+[a-z][a-z'.-]*){1,3})\s+(?:job\s+title|title)\s+(.+?)\s*$/i
const PARTY_ROLE_ENTITY_PATTERN =
  /(?:^|[;,\n]|\b\d+[.)]?\s+)(?:['"])?(licensee|licensor|supplier|customer|buyer|seller|provider|contractor)(?:['"])?\s+([a-z0-9][^;,\n…]+)/gi
const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

const isSubstantialSearchFragment = (normalizedFragment: string) => {
  if (!normalizedFragment) return false
  if (normalizedFragment.length < 14) return false
  const wordCount = normalizedFragment.split(' ').filter(Boolean).length
  const hasDigit = /\d/.test(normalizedFragment)
  return wordCount >= 3 || hasDigit
}

const getNormalizedSearchFragments = (value) =>
  String(value || '')
    .split(SEARCH_LIST_DELIMITER_PATTERN)
    .map((fragment) => normalizeForSearchComparison(fragment))
    .filter(Boolean)

const getSearchPunctuationRelaxedVariants = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const labelValueMatch = normalized.match(/^([^:]+):\s*(.+)$/)
  const valueWordCount = labelValueMatch?.[2]?.split(' ').filter(Boolean).length
  const labelToken = normalizeForSearchComparison(labelValueMatch?.[1] || '')
  const isLabelStyle = /^(?:name(?:\s*\(printed\))?|job title|title)$/.test(
    labelToken
  )
  if (
    !labelValueMatch ||
    !valueWordCount ||
    (!isLabelStyle && valueWordCount < 4)
  ) {
    return [normalized]
  }
  const collapsedLabelPunctuation = normalizeForSearchComparison(
    normalized.replace(/\s*:\s*/g, ' ')
  )
  const variants = [normalized]
  if (collapsedLabelPunctuation && collapsedLabelPunctuation !== normalized) {
    variants.push(collapsedLabelPunctuation)
  }
  if (isLabelStyle) {
    const collapsedLabelPunctuationAndCommas = normalizeForSearchComparison(
      normalized.replace(/[,:]/g, ' ')
    )
    if (
      collapsedLabelPunctuationAndCommas &&
      !variants.includes(collapsedLabelPunctuationAndCommas)
    ) {
      variants.push(collapsedLabelPunctuationAndCommas)
    }
  }
  return variants
}

const getRomanTierListFragments = (value) => {
  const raw = String(value || '')
  if (!raw.includes(',')) return []
  const tierFragments = raw
    .split(',')
    .map((fragment) =>
      normalizeForSearchComparison(stripTrailingSearchPunctuation(fragment))
    )
    .filter(Boolean)
    .flatMap((fragment) => {
      if (!ROMAN_TIER_FRAGMENT_PATTERN.test(fragment)) return []
      const withoutRomanNumerals = normalizeForSearchComparison(
        fragment.replace(/\b(?:i|ii|iii|iv|v|vi|vii|viii|ix|x)\b/gi, ' ')
      )
      const variants = [fragment]
      if (
        withoutRomanNumerals &&
        withoutRomanNumerals !== fragment &&
        withoutRomanNumerals.length >= 8
      ) {
        variants.push(withoutRomanNumerals)
      }
      return variants
    })
  const deduped = Array.from(new Set(tierFragments))
  const substantialCount = deduped.filter((fragment) =>
    ROMAN_TIER_FRAGMENT_PATTERN.test(fragment)
  ).length
  return substantialCount >= 2 ? deduped : []
}

const getSearchListFragments = (value) =>
  (() => {
    const raw = String(value || '')
    const normalizedFragments = getNormalizedSearchFragments(value)
    const romanTierFragments = getRomanTierListFragments(value)
    if (
      romanTierFragments.length > 0 &&
      !SEARCH_LIST_DELIMITER_PATTERN.test(raw)
    ) {
      return romanTierFragments
    }
    return Array.from(new Set([...normalizedFragments, ...romanTierFragments]))
  })().flatMap((fragment) => {
    const stripped = normalizeForSearchComparison(
      stripLeadingListMarker(fragment)
    )
    const fragmentCandidates =
      !stripped || stripped === fragment ? [fragment] : [fragment, stripped]
    return fragmentCandidates.flatMap((candidate) =>
      getSearchPunctuationRelaxedVariants(candidate)
    )
  })

const isProbablePersonName = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return false
  if (!PERSON_NAME_FRAGMENT_PATTERN.test(normalized)) return false
  const tokens = normalized.split(' ').filter(Boolean)
  if (tokens.length < 2 || tokens.length > 4) return false
  if (tokens.some((token) => /\d/.test(token))) return false
  return tokens.some((token) => token.length >= 3)
}

const parsePersonRolePair = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return null
  const match =
    normalized.match(PERSON_ROLE_PAREN_PATTERN) ||
    normalized.match(PERSON_ROLE_TITLE_PATTERN)
  if (!match) return null
  const personName = normalizeForSearchComparison(match[1] || '')
  const roleName = normalizeForSearchComparison(
    stripTrailingSearchPunctuation(match[2] || '')
  )
  if (!personName || !roleName) return null
  const roleWordCount = roleName.split(' ').filter(Boolean).length
  if (!isProbablePersonName(personName)) return null
  if (roleWordCount < 2 || roleName.length < 8) return null
  return { personName, roleName }
}

const getPersonRoleSearchQueries = (value) => {
  const fragments = Array.from(new Set(getNormalizedSearchFragments(value)))
  if (fragments.length === 0) return []
  const queries: string[] = []
  fragments.forEach((fragment) => {
    const parsed = parsePersonRolePair(fragment)
    if (!parsed) return
    const { personName, roleName } = parsed
    queries.push(personName)
    queries.push(`name ${personName}`)
    queries.push(`name printed ${personName}`)
    queries.push(roleName)
    queries.push(`title ${roleName}`)
    queries.push(`job title ${roleName}`)
  })
  return Array.from(new Set(queries))
}

const getPartyRoleEntitySearchQueries = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const regex = new RegExp(PARTY_ROLE_ENTITY_PATTERN.source, 'gi')
  const queries: string[] = []
  let match = regex.exec(normalized)
  while (match) {
    const roleToken = normalizeForSearchComparison(match[1] || '')
    let entityText = normalizeForSearchComparison(match[2] || '')
    if (!roleToken || !entityText) {
      match = regex.exec(normalized)
      continue
    }
    entityText =
      normalizeForSearchComparison(
        entityText.split(/(?:\.\s*){2,}|…+/)[0] || ''
      ) || entityText
    entityText = normalizeForSearchComparison(
      entityText.replace(/\(\s*company number[^)]*\)/gi, ' ')
    )
    ;[
      ' acting by and through ',
      ' acting by ',
      ' through whom ',
      ' whose ',
      ' which ',
      ' that ',
    ].forEach((splitter) => {
      const splitterIndex = entityText.indexOf(splitter)
      if (splitterIndex >= 0) {
        entityText = entityText.slice(0, splitterIndex)
      }
    })
    entityText = normalizeForSearchComparison(
      stripTrailingSearchPunctuation(entityText)
    )
    const entityWords = entityText.split(' ').filter(Boolean)
    if (entityWords.length >= 2 && entityText.length >= 8) {
      queries.push(entityText)
      queries.push(`${roleToken} ${entityText}`)
    }
    match = regex.exec(normalized)
  }
  return Array.from(new Set(queries))
}

const getLabelValueSearchQueries = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const regex = new RegExp(LABEL_VALUE_QUERY_PATTERN.source, 'gi')
  const queries: string[] = []
  let match = regex.exec(normalized)
  while (match) {
    const normalizedLabel = normalizeForSearchComparison(match[1] || '')
      .replace(/\(\s*printed\s*\)/g, ' printed')
      .replace(/\s+/g, ' ')
      .trim()
    const normalizedValue = normalizeForSearchComparison(
      stripTrailingSearchPunctuation(match[2] || '')
    )
    const valueWordCount = normalizedValue.split(' ').filter(Boolean).length
    if (normalizedLabel && normalizedValue) {
      queries.push(`${normalizedLabel} ${normalizedValue}`)
      if (normalizedLabel === 'name printed') {
        queries.push(`name ${normalizedValue}`)
      }
      if (normalizedLabel === 'title') {
        queries.push(`job title ${normalizedValue}`)
      }
      if (valueWordCount >= 2 && normalizedValue.length >= 8) {
        queries.push(normalizedValue)
      }
    }
    match = regex.exec(normalized)
  }
  return Array.from(new Set(queries))
}

const getOrdinalDaySuffix = (day: number) => {
  const dayAbs = Math.abs(day)
  if (dayAbs % 100 >= 11 && dayAbs % 100 <= 13) return 'th'
  if (dayAbs % 10 === 1) return 'st'
  if (dayAbs % 10 === 2) return 'nd'
  if (dayAbs % 10 === 3) return 'rd'
  return 'th'
}

const getIsoDateSearchVariants = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const match = normalized.match(ISO_DATE_PATTERN)
  if (!match) return []
  const year = Number.parseInt(match[1], 10)
  const month = Number.parseInt(match[2], 10)
  const day = Number.parseInt(match[3], 10)
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return []
  }
  const monthName = MONTH_NAMES[month - 1]
  const dayPlain = String(day)
  const dayPadded = String(day).padStart(2, '0')
  const monthPlain = String(month)
  const monthPadded = String(month).padStart(2, '0')
  const dayWithSuffix = `${dayPlain}${getOrdinalDaySuffix(day)}`

  const variants = [
    `${dayPlain}/${monthPlain}/${year}`,
    `${dayPlain}/${monthPadded}/${year}`,
    `${dayPadded}/${monthPlain}/${year}`,
    `${dayPadded}/${monthPadded}/${year}`,
    `${dayPlain}-${monthPlain}-${year}`,
    `${dayPlain}-${monthPadded}-${year}`,
    `${dayPadded}-${monthPlain}-${year}`,
    `${dayPadded}-${monthPadded}-${year}`,
    `${dayPlain} ${monthName} ${year}`,
    `${dayPadded} ${monthName} ${year}`,
    `${dayWithSuffix} ${monthName} ${year}`,
  ]

  return Array.from(
    new Set(
      variants.map((item) => normalizeForSearchComparison(item)).filter(Boolean)
    )
  )
}

const getLongFragmentAnchorQueries = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const words = normalized.split(' ').filter(Boolean)
  if (words.length < 8) return []
  const anchors: string[] = []
  const leading = words.slice(0, 5).join(' ').trim()
  const trailing = words.slice(-5).join(' ').trim()
  if (leading.length >= 14) anchors.push(leading)
  if (trailing.length >= 14) anchors.push(trailing)
  if (words.length >= 12) {
    const centerStart = Math.max(Math.floor(words.length / 2) - 2, 0)
    const center = words
      .slice(centerStart, centerStart + 5)
      .join(' ')
      .trim()
    if (center.length >= 14) anchors.push(center)
  }
  return Array.from(new Set(anchors))
}

const getRepeatedBigramAnchorQueries = (value) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const words = normalized.split(' ').filter(Boolean)
  if (words.length < 6) return []

  const positionsByBigram = new Map<string, number[]>()
  for (let index = 0; index < words.length - 1; index += 1) {
    const bigram = `${words[index]} ${words[index + 1]}`.trim()
    if (!bigram) continue
    const existing = positionsByBigram.get(bigram) || []
    existing.push(index)
    positionsByBigram.set(bigram, existing)
  }

  const anchors: string[] = []
  positionsByBigram.forEach((positions) => {
    if (positions.length < 2) return
    positions.forEach((index) => {
      if (index > 0) {
        const leadingTrigram = `${words[index - 1]} ${words[index]} ${
          words[index + 1]
        }`.trim()
        if (leadingTrigram.length >= 14) anchors.push(leadingTrigram)
      }
      if (index + 2 < words.length) {
        const trailingTrigram = `${words[index]} ${words[index + 1]} ${
          words[index + 2]
        }`.trim()
        if (trailingTrigram.length >= 14) anchors.push(trailingTrigram)
      }
    })
  })

  return Array.from(new Set(anchors))
}

const shouldTreatAsListSearch = (value) => {
  const raw = String(value || '')
  if (!raw) return false
  const romanTierFragments = getRomanTierListFragments(raw)
  if (romanTierFragments.length >= 2) return true
  if (!SEARCH_LIST_DELIMITER_PATTERN.test(raw)) return false
  const hasEllipsisSeparator = /(?:…+|(?:\.\s*){3,})/.test(raw)
  const allFragments = getNormalizedSearchFragments(raw)
  const substantialFragments = Array.from(
    new Set(
      allFragments.filter((fragment) => isSubstantialSearchFragment(fragment))
    )
  )
  if (hasEllipsisSeparator) {
    return allFragments.length >= 2 && substantialFragments.length >= 1
  }
  return substantialFragments.length >= 2
}

const buildSearchQueries = (value) => {
  const raw = String(value || '')
  const normalized = normalizeForSearchComparison(raw)
  if (!normalized) return []

  const strippedRaw = stripLeadingListMarker(raw)
  const normalizedStrippedRaw = normalizeForSearchComparison(strippedRaw)
  const trimmedRaw = stripTrailingSearchPunctuation(raw)
  const normalizedTrimmedRaw = normalizeForSearchComparison(trimmedRaw)
  const trimmedStrippedRaw = stripTrailingSearchPunctuation(strippedRaw)
  const normalizedTrimmedStrippedRaw =
    normalizeForSearchComparison(trimmedStrippedRaw)
  const shouldUseListFragments = shouldTreatAsListSearch(raw)
  const romanTierFragments = new Set(getRomanTierListFragments(raw))
  const queries = shouldUseListFragments
    ? []
    : getSearchPunctuationRelaxedVariants(normalized)
  if (
    !shouldUseListFragments &&
    normalizedTrimmedRaw &&
    normalizedTrimmedRaw !== normalized
  ) {
    queries.push(...getSearchPunctuationRelaxedVariants(normalizedTrimmedRaw))
  }
  if (
    !shouldUseListFragments &&
    normalizedStrippedRaw &&
    normalizedStrippedRaw !== normalized
  ) {
    queries.push(...getSearchPunctuationRelaxedVariants(normalizedStrippedRaw))
  }
  if (
    !shouldUseListFragments &&
    normalizedTrimmedStrippedRaw &&
    normalizedTrimmedStrippedRaw !== normalized &&
    normalizedTrimmedStrippedRaw !== normalizedStrippedRaw
  ) {
    queries.push(
      ...getSearchPunctuationRelaxedVariants(normalizedTrimmedStrippedRaw)
    )
  }
  if (shouldUseListFragments) {
    const fragments = getSearchListFragments(raw).filter(
      (fragment) =>
        isSubstantialSearchFragment(fragment) ||
        romanTierFragments.has(fragment)
    )
    fragments.forEach((fragment) => {
      queries.push(fragment)
    })
  }
  getLabelValueSearchQueries(raw).forEach((query) => {
    queries.push(query)
  })
  getPersonRoleSearchQueries(raw).forEach((query) => {
    queries.push(query)
  })
  getPartyRoleEntitySearchQueries(raw).forEach((query) => {
    queries.push(query)
  })
  Array.from(queries).forEach((query) => {
    getIsoDateSearchVariants(query).forEach((variant) => {
      queries.push(variant)
    })
  })
  Array.from(queries).forEach((query) => {
    getLongFragmentAnchorQueries(query).forEach((anchor) => {
      queries.push(anchor)
    })
  })
  getRepeatedBigramAnchorQueries(raw).forEach((anchor) => {
    queries.push(anchor)
  })

  return Array.from(new Set(queries))
}

const DATE_LIKE_PATTERN =
  /\b\d{1,2}(?:st|nd|rd|th)?\s+[a-z]{3,9}\s+\d{2,4}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/gi
const MONEY_LIKE_PATTERN =
  /(?:[$£€¥]\s?\d[\d,]*(?:\.\d{2})?|\b\d[\d,]*(?:\.\d{2})?\s?(?:usd|gbp|eur|cad|aud|chf|jpy)\b|\b\d{1,3}(?:,\d{3})+(?:\.\d{2})?\b|\b\d+\.\d{2}\b)/gi
const DATE_LIKE_SINGLE_PATTERN =
  /\b\d{1,2}(?:st|nd|rd|th)?\s+[a-z]{3,9}\s+\d{2,4}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/i
const MONEY_LIKE_SINGLE_PATTERN =
  /(?:[$£€¥]\s?\d[\d,]*(?:\.\d{2})?|\b\d[\d,]*(?:\.\d{2})?\s?(?:usd|gbp|eur|cad|aud|chf|jpy)\b|\b\d{1,3}(?:,\d{3})+(?:\.\d{2})?\b|\b\d+\.\d{2}\b)/i
const TOTAL_AMOUNT_PAIR_PATTERN =
  /(?:year\s+\d+\s+)?total\s+((?:[$£€¥]\s?\d[\d,]*(?:\.\d{2})?)|(?:\d[\d,]*(?:\.\d{2})?\s?(?:usd|gbp|eur|cad|aud|chf|jpy)))/gi
const YEAR_HEADER_PATTERN = /\byear\s+\d+\b/gi
const CONTRACT_VIEWER_DEBUG_STORAGE_KEY = 'contractViewerDebug'

const isAmountQueryCandidate = (queryCandidate: string) => {
  const raw = String(queryCandidate || '')
  const normalized = normalizeForSearchComparison(raw)
  if (!normalized || !/\d/.test(normalized)) return false
  if (DATE_LIKE_SINGLE_PATTERN.test(raw)) return false

  const alphaWithoutCurrencyCodes = normalized
    .replace(/\b(?:usd|gbp|eur|cad|aud|chf|jpy)\b/g, '')
    .replace(/[$£€¥\d\s,().-]/g, '')
  if (alphaWithoutCurrencyCodes) return false

  return (
    MONEY_LIKE_SINGLE_PATTERN.test(raw) || /^[\d\s,().-]+$/.test(normalized)
  )
}

const isContractViewerDebugEnabled = () => {
  if (process.env.NODE_ENV !== 'production') return true
  if (typeof window === 'undefined') return false
  const debugWindow = window as Window & { __contractViewerDebug?: boolean }
  if (debugWindow.__contractViewerDebug) return true
  try {
    return (
      window.localStorage.getItem(CONTRACT_VIEWER_DEBUG_STORAGE_KEY) === '1'
    )
  } catch (error) {
    return false
  }
}

const contractViewerDebug = (eventName: string, payload: unknown) => {
  if (!isContractViewerDebugEnabled()) return
  console.debug(`[ContractViewerDebug] ${eventName}`, payload)
}

const buildTableFallbackQueries = (value) => {
  const raw = String(value || '')
  if (!raw || shouldTreatAsListSearch(raw)) return []
  const strippedRaw = stripLeadingListMarker(raw)
  const fallbackRaw = stripTrailingSearchPunctuation(strippedRaw || raw)
  const normalized = normalizeForSearchComparison(fallbackRaw)
  if (!normalized) return []

  const generalQueries: string[] = []
  const dateQueries: string[] = []
  const totalAmountQueries: string[] = []
  const amountPairQueries: string[] = []
  const amountQueries: string[] = []
  const dateMatches = fallbackRaw.match(DATE_LIKE_PATTERN) || []
  const moneyMatches = fallbackRaw.match(MONEY_LIKE_PATTERN) || []

  const words = normalized.split(' ').filter(Boolean)
  if (words.length >= 6) {
    const trailing = words.slice(-4).join(' ')
    const leading = words.slice(0, 5).join(' ')
    if (trailing.length >= 12) generalQueries.push(trailing)
    if (leading.length >= 14) generalQueries.push(leading)
  }

  const withoutDatesAndMoney = fallbackRaw
    .replace(DATE_LIKE_PATTERN, ' ')
    .replace(MONEY_LIKE_PATTERN, ' ')
  const normalizedWithoutDates =
    normalizeForSearchComparison(withoutDatesAndMoney)
  if (normalizedWithoutDates) {
    const withoutDateWords = normalizedWithoutDates.split(' ').filter(Boolean)
    if (normalizedWithoutDates.length >= 14 && withoutDateWords.length >= 3) {
      generalQueries.push(normalizedWithoutDates)
    }
  }

  dateMatches.forEach((dateCandidate) => {
    const normalizedDate = normalizeForSearchComparison(dateCandidate)
    if (!normalizedDate) return
    dateQueries.push(normalizedDate)
  })

  const normalizedMoneyMatches = moneyMatches
    .map((moneyCandidate) => normalizeForSearchComparison(moneyCandidate))
    .filter(Boolean)
  normalizedMoneyMatches.forEach((moneyCandidate) => {
    amountQueries.push(moneyCandidate)
  })
  if (normalizedMoneyMatches.length >= 2) {
    normalizedMoneyMatches.forEach((moneyCandidate, moneyIndex) => {
      const nextMoneyCandidate = normalizedMoneyMatches[moneyIndex + 1]
      if (!nextMoneyCandidate) return
      const pairCandidate = `${moneyCandidate} ${nextMoneyCandidate}`.trim()
      if (pairCandidate.length >= 8) {
        amountPairQueries.push(pairCandidate)
      }
    })
  }

  const totalAmounts: string[] = []
  TOTAL_AMOUNT_PAIR_PATTERN.lastIndex = 0
  let totalAmountMatch = TOTAL_AMOUNT_PAIR_PATTERN.exec(fallbackRaw)
  while (totalAmountMatch) {
    const normalizedTotalAmount = normalizeForSearchComparison(
      totalAmountMatch[1]
    )
    if (normalizedTotalAmount) {
      totalAmounts.push(normalizedTotalAmount)
    }
    totalAmountMatch = TOTAL_AMOUNT_PAIR_PATTERN.exec(fallbackRaw)
  }
  if (totalAmounts.length > 0) {
    totalAmounts.forEach((amountCandidate) => {
      totalAmountQueries.push(`total ${amountCandidate}`)
    })
    if (totalAmounts.length >= 2) {
      const [firstTotalAmount, secondTotalAmount] = totalAmounts
      totalAmountQueries.push(`total ${firstTotalAmount} ${secondTotalAmount}`)
    }
  }

  return Array.from(
    new Set([
      ...generalQueries,
      ...dateQueries,
      ...totalAmountQueries,
      ...amountPairQueries,
      ...amountQueries,
    ])
  )
}

const hasNumericSeparatorPattern = (value) =>
  /\d/.test(String(value || '')) && /[.,]/.test(String(value || ''))

const stripNumericSeparators = (value) =>
  String(value || '').replace(/[.,]/g, '')

const buildAmountDigitCandidates = (value) => {
  const rawDigits = String(value || '').replace(/\D/g, '')
  if (!rawDigits) return []
  const candidates = [rawDigits]
  if (rawDigits.length > 4 && rawDigits.endsWith('00')) {
    candidates.push(rawDigits.slice(0, -2))
  }
  return Array.from(new Set(candidates))
}

const extractAmountLikeTokens = (value) => {
  const raw = String(value || '')
  if (!raw) return []
  const tokenRegex =
    /(?:[$£€¥]\s*)?\d[\d,]*(?:\.\d{2})?(?:\s?(?:usd|gbp|eur|cad|aud|chf|jpy))?/gi
  const matches = raw.match(tokenRegex) || []
  return Array.from(
    new Set(
      matches
        .map((item) => normalizeForSearchComparison(item))
        .filter((item) => /\d{3,}/.test(item.replace(/\D/g, '')))
    )
  )
}

const countAmountLikeTokenOccurrences = (value) => {
  const raw = String(value || '')
  if (!raw) return 0
  return (raw.match(MONEY_LIKE_PATTERN) || []).length
}

const getPageHintFromEvidence = (value) => {
  if (!value) return null
  const match = String(value).match(/\b(?:page|p\.)\s*(\d{1,4})\b/i)
  if (!match) return null
  const page = Number.parseInt(match[1], 10)
  if (!Number.isFinite(page) || page <= 0) return null
  return page
}

const buildViewerFragment = (evidenceText) => {
  const parts = ['zoom=page-width']
  const page = getPageHintFromEvidence(evidenceText)
  const search = normalizeEvidenceSearchText(evidenceText)
  if (page) parts.unshift(`page=${page}`)
  if (search) parts.push(`search=${encodeURIComponent(search)}`)
  return `#${parts.join('&')}`
}

const countOccurrences = (text, query) => {
  const normalizedText = normalizeForSearchComparison(text)
  const normalizedQuery = normalizeForSearchComparison(query)
  if (!normalizedText || !normalizedQuery) return 0
  let cursor = 0
  let count = 0
  while (cursor < normalizedText.length) {
    const index = normalizedText.indexOf(normalizedQuery, cursor)
    if (index === -1) break
    count += 1
    cursor = index + normalizedQuery.length
  }
  if (count > 0) return count

  if (hasNumericSeparatorPattern(normalizedQuery)) {
    const numericText = stripNumericSeparators(normalizedText)
    const numericQuery = stripNumericSeparators(normalizedQuery)
    if (!numericText || !numericQuery) return 0
    let numericCursor = 0
    while (numericCursor < numericText.length) {
      const numericIndex = numericText.indexOf(numericQuery, numericCursor)
      if (numericIndex === -1) break
      count += 1
      numericCursor = numericIndex + numericQuery.length
    }
  }
  return count
}

const resolveSearchQueriesForDocument = (
  pageTextByPage: Record<number, string>,
  query: string
) => {
  const primaryQueries = buildSearchQueries(query)
  if (primaryQueries.length === 0) return []
  const queryText = String(query || '')
  const preferStructuredFallback =
    !shouldTreatAsListSearch(queryText) &&
    (DATE_LIKE_SINGLE_PATTERN.test(queryText) ||
      MONEY_LIKE_SINGLE_PATTERN.test(queryText))
  const fallbackQueries = buildTableFallbackQueries(query)
  if (preferStructuredFallback && fallbackQueries.length > 0) {
    return Array.from(new Set([...primaryQueries, ...fallbackQueries]))
  }

  const primaryTotal = Object.values(pageTextByPage).reduce<number>(
    (sum, pageText) =>
      sum +
      primaryQueries.reduce(
        (querySum, queryCandidate) =>
          querySum + countOccurrences(pageText, queryCandidate),
        0
      ),
    0
  )
  if (primaryTotal > 0) return primaryQueries

  if (fallbackQueries.length === 0) return primaryQueries
  return fallbackQueries
}

const isLowSignalDerivedValue = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return true
  const normalized = normalizeForSearchComparison(raw)
  if (!normalized) return true

  const shortCommon = new Set([
    'it',
    'in',
    'on',
    'at',
    'to',
    'of',
    'or',
    'as',
    'is',
    'by',
    'an',
    'a',
  ])

  const hasDigit = /\d/.test(raw)
  const hasSeparator = /[\s/\-_]/.test(raw)
  const hasCurrency = /[$£€¥]/.test(raw)

  if (shortCommon.has(normalized)) return true
  if (normalized.length <= 3 && !hasDigit && !hasSeparator && !hasCurrency) {
    return true
  }
  return false
}

const isLikelyHeuristicText = (value) => {
  const normalized = String(value || '').toLowerCase()
  if (!normalized) return false
  return (
    normalized.includes('heuristic') ||
    normalized.includes('fallback') ||
    normalized.includes('document-type heuristic') ||
    normalized.includes('identifier heuristic') ||
    normalized.includes('month-year heuristic') ||
    normalized.includes('date-range heuristic')
  )
}

const getTotalMatchesInDocument = (
  pageTextByPage: Record<number, string>,
  query: string
) => {
  const queries = resolveSearchQueriesForDocument(pageTextByPage, query)
  if (queries.length === 0) return 0
  return Object.values(pageTextByPage).reduce<number>(
    (sum, pageText) =>
      sum +
      queries.reduce(
        (querySum, queryCandidate) =>
          querySum + countOccurrences(pageText, queryCandidate),
        0
      ),
    0
  )
}

type OcrLayoutBox = {
  left: number
  top: number
  width: number
  height: number
}

type OcrLayoutLine = {
  text: string
  bbox: OcrLayoutBox
}

type OcrLayoutWord = {
  text: string
  bbox: OcrLayoutBox
}

type OcrLayoutPage = {
  page_number?: number
  lines?: Array<{
    text?: string
    bbox?: {
      left?: number
      top?: number
      width?: number
      height?: number
    }
  }>
  words?: Array<{
    text?: string
    bbox?: {
      left?: number
      top?: number
      width?: number
      height?: number
    }
  }>
}

type OcrLayoutPayload = {
  pages?: OcrLayoutPage[]
}

type ViewerMatch = {
  pageNumber: number
  indexOnPage: number
  lineIndexes?: number[]
}

type OcrSingleCandidate = {
  pageNumber: number
  lineIndexes: number[]
  score: number
  selectionReason?: string
  queryCandidate?: string
}

type OcrLineGroupProfile = {
  left: number
  right: number
  centerY: number
  averageHeight: number
}

type OcrSectionExpansionContext = {
  searchQueries: string[]
  searchTokens: string[]
  phraseAnchors: string[]
  exactSearchQueries: string[]
}

type OcrExpandedLineIndexes = {
  lineIndexes: number[]
  continuationLineIndexes: number[]
  queryContextLineIndexes: number[]
}

type OcrWordHighlightBox = {
  lineIndex: number
  bbox: OcrLayoutBox
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value))

const normalizeOcrLayoutBox = (
  raw:
    | {
        left?: number
        top?: number
        width?: number
        height?: number
      }
    | undefined
): OcrLayoutBox | null => {
  if (!raw) return null
  const left = Number(raw.left)
  const top = Number(raw.top)
  const width = Number(raw.width)
  const height = Number(raw.height)
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null
  }
  return {
    left: clamp01(left),
    top: clamp01(top),
    width: clamp01(width),
    height: clamp01(height),
  }
}

const extractOcrLinesByPage = (
  payload: OcrLayoutPayload | null | undefined
): Record<number, OcrLayoutLine[]> => {
  if (!payload || !Array.isArray(payload.pages)) return {}
  const next: Record<number, OcrLayoutLine[]> = {}
  payload.pages.forEach((page) => {
    const pageNumber = Number(page?.page_number)
    if (!Number.isFinite(pageNumber) || pageNumber <= 0) return
    const lines = Array.isArray(page?.lines) ? page.lines : []
    const normalizedLines: OcrLayoutLine[] = []
    lines.forEach((line) => {
      const text = normalizeForSearchComparison(line?.text || '')
      if (!text) return
      const bbox = normalizeOcrLayoutBox(line?.bbox)
      if (!bbox) return
      normalizedLines.push({ text, bbox })
    })
    if (normalizedLines.length > 0) {
      next[pageNumber] = normalizedLines
    }
  })
  return next
}

const extractOcrWordsByPage = (
  payload: OcrLayoutPayload | null | undefined
): Record<number, OcrLayoutWord[]> => {
  if (!payload || !Array.isArray(payload.pages)) return {}
  const next: Record<number, OcrLayoutWord[]> = {}
  payload.pages.forEach((page) => {
    const pageNumber = Number(page?.page_number)
    if (!Number.isFinite(pageNumber) || pageNumber <= 0) return
    const words = Array.isArray(page?.words) ? page.words : []
    const normalizedWords: OcrLayoutWord[] = []
    words.forEach((word) => {
      const text = normalizeForSearchComparison(word?.text || '')
      if (!text) return
      const bbox = normalizeOcrLayoutBox(word?.bbox)
      if (!bbox) return
      normalizedWords.push({ text, bbox })
    })
    if (normalizedWords.length > 0) {
      next[pageNumber] = normalizedWords
    }
  })
  return next
}

const getOcrLineCenter = (line: OcrLayoutLine) => ({
  x: line.bbox.left + line.bbox.width / 2,
  y: line.bbox.top + line.bbox.height / 2,
})

const getOcrLineVerticalCenter = (line: OcrLayoutLine) =>
  line.bbox.top + line.bbox.height / 2

const getOcrLineBottom = (line: OcrLayoutLine) =>
  line.bbox.top + line.bbox.height

const getOcrLineRight = (line: OcrLayoutLine) =>
  line.bbox.left + line.bbox.width

const getOcrHorizontalOverlapRatio = (a: OcrLayoutLine, b: OcrLayoutLine) => {
  const left = Math.max(a.bbox.left, b.bbox.left)
  const right = Math.min(getOcrLineRight(a), getOcrLineRight(b))
  const overlap = Math.max(0, right - left)
  const minWidth = Math.max(0.001, Math.min(a.bbox.width, b.bbox.width))
  return overlap / minWidth
}

const getOcrHorizontalContainmentInRange = (
  line: OcrLayoutLine,
  rangeLeft: number,
  rangeRight: number
) => {
  const left = Math.max(line.bbox.left, rangeLeft)
  const right = Math.min(getOcrLineRight(line), rangeRight)
  const overlap = Math.max(0, right - left)
  return overlap / Math.max(0.001, line.bbox.width)
}

const OCR_QUERY_STOP_WORDS = new Set([
  'the',
  'and',
  'for',
  'that',
  'from',
  'with',
  'this',
  'shall',
  'will',
  'into',
  'such',
  'thereof',
  'therein',
  'hereby',
  'under',
  'between',
  'party',
  'parties',
  'agreement',
  'date',
])

const getInformativeOcrSearchTokens = (value: string) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  return Array.from(
    new Set(
      normalized
        .split(' ')
        .map((token) => token.trim())
        .filter(
          (token) =>
            token.length >= 3 &&
            !OCR_QUERY_STOP_WORDS.has(token) &&
            !/^\d+$/.test(token)
        )
    )
  )
}

const buildOcrPhraseAnchors = (value: string) => {
  const normalized = normalizeForSearchComparison(value)
  if (!normalized) return []
  const words = normalized.split(' ').filter(Boolean)
  if (words.length < 3) return []

  const anchors = new Set<string>()
  ;[4, 3].forEach((windowSize) => {
    if (words.length < windowSize) return
    for (let index = 0; index <= words.length - windowSize; index += 1) {
      const phraseWords = words.slice(index, index + windowSize)
      const informativeCount = phraseWords.filter(
        (word) => !OCR_QUERY_STOP_WORDS.has(word)
      ).length
      if (informativeCount < 2) continue
      const phrase = phraseWords.join(' ')
      if (phrase.length < 12) continue
      anchors.add(phrase)
    }
  })
  return Array.from(anchors)
}

const buildOcrExactSearchQueries = (searchTerm: string) => {
  const candidates = [
    searchTerm,
    stripTrailingSearchPunctuation(searchTerm),
    stripLeadingListMarker(searchTerm),
    stripTrailingSearchPunctuation(stripLeadingListMarker(searchTerm)),
  ]
    .map((candidate) => normalizeForSearchComparison(candidate))
    .filter(Boolean)

  return Array.from(new Set(candidates))
}

const lineMatchesOcrQueryContext = (
  line: OcrLayoutLine,
  context: OcrSectionExpansionContext
) => {
  if (
    context.searchQueries.some(
      (queryCandidate) => countOccurrences(line.text, queryCandidate) > 0
    )
  ) {
    return true
  }
  if (
    context.phraseAnchors.some(
      (phraseAnchor) => countOccurrences(line.text, phraseAnchor) > 0
    )
  ) {
    return true
  }
  if (context.searchTokens.length === 0) return false

  const lineTokens = new Set(
    normalizeForSearchComparison(line.text)
      .split(' ')
      .map((token) => token.trim())
      .filter(Boolean)
  )
  const matchedTokenCount = context.searchTokens.filter((token) =>
    lineTokens.has(token)
  ).length
  const minimumTokenHits =
    context.searchTokens.length >= 8
      ? 3
      : Math.min(2, context.searchTokens.length)
  return matchedTokenCount >= minimumTokenHits
}

const OCR_SENTENCE_CONTINUATION_STARTERS = new Set([
  'to',
  'and',
  'or',
  'as',
  'by',
  'with',
  'for',
  'of',
  'in',
  'on',
  'at',
  'from',
  'into',
  'under',
  'within',
  'without',
  'including',
  'such',
  'that',
  'which',
  'who',
  'whom',
  'whose',
  'where',
  'when',
  'if',
  'unless',
  'the',
  'a',
  'an',
])

const OCR_HEADING_LIKE_STARTERS = new Set([
  'section',
  'schedule',
  'annex',
  'appendix',
  'clause',
  'article',
  'part',
  'table',
  'figure',
])

const trimOcrTokenEdges = (token: string) =>
  String(token || '').replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')

const OCR_CONTINUATION_ENDINGS = new Set([
  'the',
  'a',
  'an',
  'of',
  'to',
  'in',
  'on',
  'for',
  'by',
  'with',
  'from',
  'as',
  'and',
  'or',
  'against',
  'between',
  'within',
  'without',
])

const OCR_LEADING_ARTICLES = new Set([
  'the',
  'a',
  'an',
  'this',
  'that',
  'these',
  'those',
])

const lineLooksLikeSentenceContinuation = (
  anchorLine: OcrLayoutLine,
  candidateLine: OcrLayoutLine
) => {
  const anchorText = normalizeForSearchComparison(anchorLine.text).trim()
  const candidateText = normalizeForSearchComparison(candidateLine.text).trim()
  if (!anchorText || !candidateText) return false
  if (/[.!?;:]\s*$/.test(anchorText)) return false

  const firstToken = trimOcrTokenEdges(candidateText.split(' ')[0] || '')
  if (!firstToken) return false
  if (/^\d+[.)]?$/.test(firstToken)) return false
  if (OCR_HEADING_LIKE_STARTERS.has(firstToken)) return false
  const anchorWords = anchorText.split(' ').filter(Boolean)
  const anchorLastToken = trimOcrTokenEdges(
    anchorWords[anchorWords.length - 1] || ''
  )
  const anchorEndsWithContinuationJoiner =
    Boolean(anchorLastToken) && OCR_CONTINUATION_ENDINGS.has(anchorLastToken)

  return (
    anchorEndsWithContinuationJoiner ||
    OCR_SENTENCE_CONTINUATION_STARTERS.has(firstToken) ||
    firstToken.length <= 3
  )
}

const getOcrLineTokenSet = (line: OcrLayoutLine) =>
  new Set(
    normalizeForSearchComparison(line.text)
      .split(' ')
      .map((token) => trimOcrTokenEdges(token))
      .filter(Boolean)
  )

const getMatchedSearchTokensForLineIndexes = (
  lines: OcrLayoutLine[],
  lineIndexes: number[],
  searchTokens: string[]
) => {
  if (searchTokens.length === 0 || lineIndexes.length === 0) {
    return new Set<string>()
  }
  const searchTokenSet = new Set(searchTokens)
  const matchedTokens = new Set<string>()
  lineIndexes.forEach((lineIndex) => {
    const line = lines[lineIndex]
    if (!line) return
    getOcrLineTokenSet(line).forEach((token) => {
      if (searchTokenSet.has(token)) {
        matchedTokens.add(token)
      }
    })
  })
  return matchedTokens
}

const lineAddsNewSearchToken = (
  line: OcrLayoutLine,
  existingTokens: Set<string>,
  searchTokens: string[]
) => {
  if (searchTokens.length === 0) return false
  const searchTokenSet = new Set(searchTokens)
  let hasNewToken = false
  getOcrLineTokenSet(line).forEach((token) => {
    if (hasNewToken) return
    if (searchTokenSet.has(token) && !existingTokens.has(token)) {
      hasNewToken = true
    }
  })
  return hasNewToken
}

const selectedOcrLinesContainExactQuery = (
  lines: OcrLayoutLine[],
  lineIndexes: number[],
  exactSearchQueries: string[]
) => {
  if (lineIndexes.length === 0 || exactSearchQueries.length === 0) return false
  const combinedText = normalizeForSearchComparison(
    lineIndexes.map((lineIndex) => lines[lineIndex]?.text || '').join(' ')
  )
  if (!combinedText) return false
  return exactSearchQueries.some(
    (queryCandidate) =>
      queryCandidate.length >= 12 &&
      countOccurrences(combinedText, queryCandidate) > 0
  )
}

const groupOcrWordsByLine = (
  lines: OcrLayoutLine[],
  words: OcrLayoutWord[]
) => {
  const wordsByLine: OcrLayoutWord[][] = lines.map(() => [])
  if (lines.length === 0 || words.length === 0) return wordsByLine

  const verticalTolerance = 0.004
  words.forEach((word) => {
    const wordCenterY = getOcrLineVerticalCenter(word)
    let bestLineIndex = -1
    let bestDistance = Number.POSITIVE_INFINITY

    lines.forEach((line, lineIndex) => {
      const top = line.bbox.top - verticalTolerance
      const bottom = getOcrLineBottom(line) + verticalTolerance
      if (wordCenterY < top || wordCenterY > bottom) return
      const distance = Math.abs(wordCenterY - getOcrLineVerticalCenter(line))
      if (distance < bestDistance) {
        bestDistance = distance
        bestLineIndex = lineIndex
      }
    })

    if (bestLineIndex >= 0) {
      wordsByLine[bestLineIndex].push(word)
    }
  })

  wordsByLine.forEach((lineWords) => {
    lineWords.sort((a, b) => a.bbox.left - b.bbox.left)
  })
  return wordsByLine
}

const buildOcrSearchTokenSets = (
  searchTerm: string,
  searchQueries: string[]
) => {
  const allTokens = new Set<string>()
  const informativeTokens = new Set<string>()
  ;[searchTerm, ...searchQueries].forEach((value) => {
    normalizeForSearchComparison(value)
      .split(' ')
      .map((token) => trimOcrTokenEdges(token))
      .filter(Boolean)
      .forEach((token) => {
        allTokens.add(token)
        if (
          token.length >= 3 &&
          !OCR_QUERY_STOP_WORDS.has(token) &&
          !/^\d+$/.test(token)
        ) {
          informativeTokens.add(token)
        }
      })
  })
  return { allTokens, informativeTokens }
}

const buildMergedOcrHighlightBox = (
  line: OcrLayoutLine,
  lineWords: OcrLayoutWord[],
  startIndex: number,
  endIndex: number
): OcrLayoutBox => {
  const selectedWords = lineWords.slice(startIndex, endIndex + 1)
  if (selectedWords.length === 0) return line.bbox

  const left = Math.min(...selectedWords.map((word) => word.bbox.left))
  const right = Math.max(...selectedWords.map((word) => getOcrLineRight(word)))
  const top = Math.min(...selectedWords.map((word) => word.bbox.top))
  const bottom = Math.max(
    ...selectedWords.map((word) => getOcrLineBottom(word))
  )

  const spanWidth = Math.max(0.001, right - left)
  const spanHeight = Math.max(0.001, bottom - top)
  const horizontalPadding = Math.min(0.006, Math.max(0.0012, spanWidth * 0.02))
  const verticalPadding = Math.min(0.004, Math.max(0.0012, spanHeight * 0.26))

  const paddedLeft = clamp01(left - horizontalPadding)
  const paddedTop = clamp01(top - verticalPadding)
  const paddedRight = clamp01(right + horizontalPadding)
  const paddedBottom = clamp01(bottom + verticalPadding)

  return {
    left: paddedLeft,
    top: paddedTop,
    width: Math.max(0.001, paddedRight - paddedLeft),
    height: Math.max(0.001, paddedBottom - paddedTop),
  }
}

const buildOcrWordHighlightBoxes = ({
  lines,
  words,
  lineIndexes,
  searchTerm,
  searchQueries,
}: {
  lines: OcrLayoutLine[]
  words: OcrLayoutWord[]
  lineIndexes: number[]
  searchTerm: string
  searchQueries: string[]
}): OcrWordHighlightBox[] => {
  if (lineIndexes.length === 0) return []
  const wordsByLine = groupOcrWordsByLine(lines, words)
  const { allTokens, informativeTokens } = buildOcrSearchTokenSets(
    searchTerm,
    searchQueries
  )
  if (allTokens.size === 0) return []
  const boundaryTokens =
    informativeTokens.size > 0 ? informativeTokens : allTokens

  const result: OcrWordHighlightBox[] = []
  const sortedLineIndexes = Array.from(
    new Set(
      lineIndexes.filter(
        (lineIndex) =>
          Number.isFinite(lineIndex) &&
          lineIndex >= 0 &&
          lineIndex < lines.length
      )
    )
  ).sort((a, b) => a - b)
  if (sortedLineIndexes.length === 0) return result
  const firstLineIndex = sortedLineIndexes[0]
  const lastLineIndex = sortedLineIndexes[sortedLineIndexes.length - 1]

  sortedLineIndexes.forEach((lineIndex) => {
    const line = lines[lineIndex]
    if (!line) return
    const lineWords = wordsByLine[lineIndex] || []
    if (lineWords.length === 0) {
      result.push({ lineIndex, bbox: line.bbox })
      return
    }

    const queryMatchPositions: number[] = []
    lineWords.forEach((word, wordIndex) => {
      const token = trimOcrTokenEdges(word.text)
      if (!token) return
      if (boundaryTokens.has(token)) {
        queryMatchPositions.push(wordIndex)
      }
    })

    if (queryMatchPositions.length === 0) {
      result.push({ lineIndex, bbox: line.bbox })
      return
    }

    let start = 0
    let end = lineWords.length - 1
    const [firstQueryMatchPosition] = queryMatchPositions
    const lastQueryMatchPosition =
      queryMatchPositions[queryMatchPositions.length - 1]

    if (lineIndex === firstLineIndex) {
      start = firstQueryMatchPosition
    }

    if (lineIndex === lastLineIndex) {
      end = lastQueryMatchPosition
    }

    while (end + 1 < lineWords.length) {
      const nextToken = trimOcrTokenEdges(lineWords[end + 1].text)
      if (!nextToken || !boundaryTokens.has(nextToken)) break
      end += 1
    }

    if (lineIndex === lastLineIndex) {
      let trailingCarryCount = 0
      const maxTrailingCarryTokens = 2
      while (
        trailingCarryCount < maxTrailingCarryTokens &&
        end + 1 < lineWords.length
      ) {
        const nextToken = trimOcrTokenEdges(lineWords[end + 1].text)
        if (
          !nextToken ||
          !allTokens.has(nextToken) ||
          boundaryTokens.has(nextToken)
        ) {
          break
        }
        const isLowSignalCarryToken =
          OCR_QUERY_STOP_WORDS.has(nextToken) ||
          /^\d+$/.test(nextToken) ||
          nextToken.length <= 2
        if (!isLowSignalCarryToken) break
        end += 1
        trailingCarryCount += 1
      }
    }

    if (lineIndex === firstLineIndex && start > 0) {
      let leadingCarryCount = 0
      const maxLeadingCarryTokens = 2
      while (leadingCarryCount < maxLeadingCarryTokens && start > 0) {
        const previousToken = trimOcrTokenEdges(lineWords[start - 1].text)
        if (
          !previousToken ||
          !allTokens.has(previousToken) ||
          boundaryTokens.has(previousToken)
        ) {
          break
        }
        const isLowSignalCarryToken =
          OCR_QUERY_STOP_WORDS.has(previousToken) ||
          OCR_LEADING_ARTICLES.has(previousToken) ||
          /^\d+$/.test(previousToken) ||
          previousToken.length <= 2
        if (!isLowSignalCarryToken) break
        start -= 1
        leadingCarryCount += 1
      }
    }
    if (start > end) {
      start = Math.max(0, Math.min(start, lineWords.length - 1))
      end = Math.max(start, Math.min(end, lineWords.length - 1))
    }

    result.push({
      lineIndex,
      bbox: buildMergedOcrHighlightBox(line, lineWords, start, end),
    })
  })

  return result
}

const buildOcrLineGroupProfile = (
  lines: OcrLayoutLine[],
  lineIndexes: number[]
): OcrLineGroupProfile | null => {
  const selectedLines = lineIndexes
    .map((index) => lines[index])
    .filter((line): line is OcrLayoutLine => Boolean(line))
  if (selectedLines.length === 0) return null

  const left = Math.min(...selectedLines.map((line) => line.bbox.left))
  const right = Math.max(...selectedLines.map((line) => getOcrLineRight(line)))
  const top = Math.min(...selectedLines.map((line) => line.bbox.top))
  const bottom = Math.max(
    ...selectedLines.map((line) => getOcrLineBottom(line))
  )
  const averageHeight =
    selectedLines.reduce((sum, line) => sum + line.bbox.height, 0) /
    selectedLines.length

  return {
    left,
    right: Math.max(right, left + 0.001),
    centerY: (top + bottom) / 2,
    averageHeight: Math.max(0.001, averageHeight),
  }
}

const lineMatchesOcrGroupProfile = (
  line: OcrLayoutLine,
  profile: OcrLineGroupProfile
) => {
  const lineHeightRatio = line.bbox.height / profile.averageHeight
  if (lineHeightRatio < 0.55 || lineHeightRatio > 1.9) return false

  const verticalDistance = Math.abs(
    getOcrLineVerticalCenter(line) - profile.centerY
  )
  const maxVerticalDistance = Math.max(0.016, profile.averageHeight * 2.6)
  if (verticalDistance > maxVerticalDistance) return false

  const containment = getOcrHorizontalContainmentInRange(
    line,
    profile.left,
    profile.right
  )
  const leftDelta = Math.abs(line.bbox.left - profile.left)
  const rightDelta = Math.abs(getOcrLineRight(line) - profile.right)

  return containment >= 0.55 || leftDelta <= 0.05 || rightDelta <= 0.2
}

const areOcrLinesInSameSection = (a: OcrLayoutLine, b: OcrLayoutLine) => {
  const verticalGap = Math.max(
    0,
    Math.max(a.bbox.top, b.bbox.top) -
      Math.min(getOcrLineBottom(a), getOcrLineBottom(b))
  )
  const averageHeight = (a.bbox.height + b.bbox.height) / 2
  const maxVerticalGap = Math.max(0.008, averageHeight * 1.8)
  const overlapRatio = getOcrHorizontalOverlapRatio(a, b)
  const leftDelta = Math.abs(a.bbox.left - b.bbox.left)
  const rightDelta = Math.abs(getOcrLineRight(a) - getOcrLineRight(b))

  return (
    verticalGap <= maxVerticalGap &&
    (overlapRatio >= 0.2 || (leftDelta <= 0.08 && rightDelta <= 0.12))
  )
}

const expandOcrLineIndexesToSection = (
  lines: OcrLayoutLine[],
  seedLineIndexes: number[],
  context: OcrSectionExpansionContext
) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    return {
      lineIndexes: [],
      continuationLineIndexes: [],
      queryContextLineIndexes: [],
    } satisfies OcrExpandedLineIndexes
  }
  const expanded = new Set(
    seedLineIndexes.filter(
      (index) => Number.isFinite(index) && index >= 0 && index < lines.length
    )
  )
  if (expanded.size === 0) {
    return {
      lineIndexes: [],
      continuationLineIndexes: [],
      queryContextLineIndexes: [],
    } satisfies OcrExpandedLineIndexes
  }
  const seedProfile = buildOcrLineGroupProfile(lines, Array.from(expanded))
  if (!seedProfile) {
    return {
      lineIndexes: [],
      continuationLineIndexes: [],
      queryContextLineIndexes: [],
    } satisfies OcrExpandedLineIndexes
  }
  const continuationLineIndexes = new Set<number>()
  const queryContextLineIndexes = new Set<number>()
  expanded.forEach((lineIndex) => {
    const line = lines[lineIndex]
    if (line && lineMatchesOcrQueryContext(line, context)) {
      queryContextLineIndexes.add(lineIndex)
    }
  })

  const maxSectionLines = 6
  let didChange = true
  while (didChange && expanded.size < maxSectionLines) {
    didChange = false
    const sorted = Array.from(expanded).sort((a, b) => a - b)
    if (
      selectedOcrLinesContainExactQuery(
        lines,
        sorted,
        context.exactSearchQueries
      )
    ) {
      break
    }
    const matchedTokens = getMatchedSearchTokensForLineIndexes(
      lines,
      sorted,
      context.searchTokens
    )
    const highest = sorted[sorted.length - 1]
    const activeProfile = buildOcrLineGroupProfile(lines, sorted) || seedProfile

    const nextIndex = highest + 1
    if (
      nextIndex < lines.length &&
      !expanded.has(nextIndex) &&
      expanded.size < maxSectionLines
    ) {
      const nextLine = lines[nextIndex]
      const anchorLine = lines[highest]
      const matchesQueryContext =
        nextLine && lineMatchesOcrQueryContext(nextLine, context)
      const matchesSentenceContinuation =
        nextLine &&
        anchorLine &&
        lineLooksLikeSentenceContinuation(anchorLine, nextLine)
      const matchesGroupProfile =
        nextLine && lineMatchesOcrGroupProfile(nextLine, activeProfile)
      const addsNewSearchToken =
        nextLine &&
        lineAddsNewSearchToken(nextLine, matchedTokens, context.searchTokens)
      if (
        nextLine &&
        anchorLine &&
        (matchesQueryContext || matchesSentenceContinuation) &&
        areOcrLinesInSameSection(anchorLine, nextLine) &&
        (matchesGroupProfile || matchesSentenceContinuation) &&
        (matchesSentenceContinuation || addsNewSearchToken)
      ) {
        expanded.add(nextIndex)
        if (matchesQueryContext) queryContextLineIndexes.add(nextIndex)
        if (!matchesQueryContext && matchesSentenceContinuation) {
          continuationLineIndexes.add(nextIndex)
        }
        didChange = true
      }
    }
  }

  return {
    lineIndexes: Array.from(expanded).sort((a, b) => a - b),
    continuationLineIndexes: Array.from(continuationLineIndexes).sort(
      (a, b) => a - b
    ),
    queryContextLineIndexes: Array.from(queryContextLineIndexes).sort(
      (a, b) => a - b
    ),
  }
}

const getTotalMatchesInOcrLayout = (
  ocrLinesByPage: Record<number, OcrLayoutLine[]>,
  query: string
) => {
  const primaryQueries = buildSearchQueries(query)
  if (primaryQueries.length === 0) return 0
  const fallbackQueries = buildTableFallbackQueries(query)
  const candidateQueries = Array.from(
    new Set([...primaryQueries, ...fallbackQueries])
  ).filter(Boolean)
  if (candidateQueries.length === 0) return 0
  return Object.values(ocrLinesByPage).reduce((sum, lines) => {
    const lineTotal = lines.reduce((lineSum, line) => {
      return (
        lineSum +
        candidateQueries.reduce(
          (querySum, queryCandidate) =>
            querySum + countOccurrences(line.text, queryCandidate),
          0
        )
      )
    }, 0)
    return sum + lineTotal
  }, 0)
}

const MetadataFieldLabel = ({ field }) => {
  const isMandatory = isFieldMandatory(field.field_name)

  return (
    <Box component="span">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="subtitle2"
          component="span"
          sx={{
            fontWeight: 600,
            color: '#1f2937',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'unset',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            lineHeight: 1.3,
            maxWidth: '100%',
          }}
        >
          {field.field_name}
        </Typography>
        {field.evidence_quote && (
          <Tooltip title={field.evidence_quote} arrow placement="top">
            <IconButton
              size="small"
              sx={{
                color: '#6b7280',
                p: 0.25,
                '&:hover': { backgroundColor: '#f3f4f6' },
              }}
            >
              <InformationCircleIcon className="h-4 w-4 text-stone-400" />
            </IconButton>
          </Tooltip>
        )}
        {field.is_verified && (
          <CheckCircleIcon className="h-4 w-4 text-green-500" />
        )}
      </Box>
      {isMandatory && (
        <Chip
          component="span"
          label="CORE REVIEW"
          size="small"
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            backgroundColor: '#7c2d12',
            color: '#fff',
            fontWeight: 'bold',
            letterSpacing: '0.04em',
          }}
        />
      )}
    </Box>
  )
}

const ContractSummaryHeader = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [reprocessLoading, setReprocessLoading] = React.useState(false)
  const [reprocessConfirmOpen, setReprocessConfirmOpen] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)

  if (!record) return null

  const queueReprocess = () => {
    setReprocessLoading(true)
    const forceRetry = record.status === 'processing'
    dataProvider
      .retryExtraction(record.id, forceRetry)
      .then(() => {
        notify('Contract reprocessing queued', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error queueing reprocess: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setReprocessLoading(false)
      })
  }

  const handleReprocess = () => {
    const hasExtractedValues = (record.metadata_fields || []).some((row) => {
      const value = row?.field_value
      return (
        value !== null && value !== undefined && String(value).trim() !== ''
      )
    })

    if (hasExtractedValues) {
      setReprocessConfirmOpen(true)
      return
    }

    queueReprocess()
  }

  const handleConfirmReprocess = () => {
    setReprocessConfirmOpen(false)
    queueReprocess()
  }

  const handleCloseReprocessConfirm = () => {
    setReprocessConfirmOpen(false)
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewBlob(record.id)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  const statusLabelMap = {
    uploaded: 'UPLOADED',
    processing: 'PROCESSING',
    review_required: 'REVIEW REQUIRED',
    completed: 'VERIFIED',
    failed: 'FAILED',
    extraction_failed: 'EXTRACTION FAILED',
    missing_source_file: 'MISSING SOURCE FILE',
  }

  const stats = {
    total: record.metadata_fields?.length || 0,
    verified: record.metadata_fields?.filter((f) => f.is_verified).length || 0,
    confidence:
      typeof record.ai_confidence === 'number'
        ? Math.round(record.ai_confidence * 100)
        : 0,
  }
  const terminalNoDataFailure =
    typeof record.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))
  const canReprocess =
    record.status !== 'missing_source_file' &&
    !(record.status === 'failed' && terminalNoDataFailure)
  const actionLabel =
    record.status === 'failed' || record.status === 'extraction_failed'
      ? 'Retry'
      : 'Reprocess'

  const confidenceColor = getConfidenceColor(stats.confidence)

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderColor: '#e5e7eb',
          borderRadius: 2,
          background:
            'linear-gradient(135deg, rgba(249,250,251,1) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="overline" color="textSecondary">
                Contract Name
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#111827' }}
              >
                {record.filename}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="overline" color="textSecondary">
                Avg Confidence
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: confidenceColor,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {stats.confidence}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="overline" color="textSecondary">
                Verification
              </Typography>
              <Typography variant="h6">
                {stats.verified} / {stats.total}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="overline" color="textSecondary">
                    Current Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={statusLabelMap[record.status] || 'UNKNOWN'}
                      color={
                        record.status === 'failed' ||
                        record.status === 'extraction_failed'
                          ? 'error'
                          : record.status === 'review_required'
                          ? 'warning'
                          : record.status === 'completed'
                          ? 'success'
                          : 'default'
                      }
                      variant="filled"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  {(record.status === 'uploaded' ||
                    record.status === 'processing') &&
                    (record.extraction_stage_label ||
                      record.extraction_status_message) && (
                      <Box mt={1}>
                        {record.extraction_stage_label && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            {record.extraction_stage_label}
                            {record.extraction_stage_index &&
                            record.extraction_stage_total
                              ? ` (${record.extraction_stage_index}/${record.extraction_stage_total})`
                              : ''}
                          </Typography>
                        )}
                        {record.extraction_status_message && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            {record.extraction_status_message}
                          </Typography>
                        )}
                      </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip
                    title={
                      previewLoading ? 'Opening preview...' : 'Preview PDF'
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handlePreview}
                        disabled={previewLoading}
                        aria-label="Preview PDF"
                        sx={{
                          border: '1px solid #d1d5db',
                          borderRadius: 1,
                          width: 32,
                          height: 32,
                          color: '#4b5563',
                          '&:hover': {
                            backgroundColor: '#f9fafb',
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canReprocess && (
                    <Tooltip
                      title={
                        reprocessLoading
                          ? `Queueing ${actionLabel.toLowerCase()}...`
                          : actionLabel
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleReprocess}
                          disabled={reprocessLoading}
                          aria-label={actionLabel}
                          sx={{
                            border: '1px solid #d1d5db',
                            borderRadius: 1,
                            width: 32,
                            height: 32,
                            color:
                              record.status === 'uploaded' ||
                              record.status === 'review_required'
                                ? '#1d4ed8'
                                : '#b91c1c',
                            '&:hover': {
                              backgroundColor: '#f9fafb',
                            },
                          }}
                        >
                          <ArrowPathIcon
                            className={`h-4 w-4 ${
                              reprocessLoading ? 'animate-spin' : ''
                            }`}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Confirm
        isOpen={reprocessConfirmOpen}
        title="Reprocess Contract"
        content="Existing extracted metadata values will be replaced. Continue?"
        onConfirm={handleConfirmReprocess}
        onClose={handleCloseReprocessConfirm}
      />
    </>
  )
}

const ContractStatusPolling = () => {
  const record = useRecordContext()
  const refresh = useRefresh()

  React.useEffect(() => {
    if (!record) return undefined

    const inProgressStatuses = ['uploaded', 'processing']
    if (!inProgressStatuses.includes(record.status)) return undefined

    const intervalId = window.setInterval(() => {
      refresh()
    }, 4000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [record, record?.status, refresh])

  return null
}

const ContractDocumentViewer = ({
  contractId,
  isOpen,
  evidenceText,
  extractedValue,
  evidenceFieldName,
  onClose,
}) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState('')
  const [loadingPreview, setLoadingPreview] = React.useState(false)
  const [loadingPdf, setLoadingPdf] = React.useState(false)
  const [fallbackText, setFallbackText] = React.useState('')
  const [fallbackTruncated, setFallbackTruncated] = React.useState(false)
  const [sourceMissing, setSourceMissing] = React.useState(false)
  const [ocrLinesByPage, setOcrLinesByPage] = React.useState<
    Record<number, OcrLayoutLine[]>
  >({})
  const [ocrWordsByPage, setOcrWordsByPage] = React.useState<
    Record<number, OcrLayoutWord[]>
  >({})
  const [parsedTextSource, setParsedTextSource] = React.useState('')
  const [numPages, setNumPages] = React.useState(0)
  const [scale, setScale] = React.useState(1)
  const [pageTextByPage, setPageTextByPage] = React.useState<
    Record<number, string>
  >({})
  const [searchTerm, setSearchTerm] = React.useState('')
  const [resolvedAutoSearchKey, setResolvedAutoSearchKey] = React.useState('')
  const [activeMatchIndex, setActiveMatchIndex] = React.useState(0)
  const [highlightCountsByPage, setHighlightCountsByPage] = React.useState<
    Record<number, number>
  >({})
  const pageRefs = React.useRef<Record<number, HTMLDivElement | null>>({})
  const highlightRetryCountRef = React.useRef<Record<number, number>>({})
  const highlightRetryTimerRef = React.useRef<Record<number, number>>({})
  const stableEffectiveSearchTermRef = React.useRef('')
  const evidencePreview = normalizeEvidenceSearchText(evidenceText)
  const valuePreview = normalizeEvidenceSearchText(extractedValue)
  const pageHint = getPageHintFromEvidence(evidenceText)
  const autoSearchKey = `${valuePreview}||${evidencePreview}`

  const scrollToPage = React.useCallback((pageNumber: number) => {
    const target = pageRefs.current[pageNumber]
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const clearHighlightRetryTimers = React.useCallback(() => {
    Object.values(highlightRetryTimerRef.current).forEach((timerId) => {
      window.clearTimeout(timerId)
    })
    highlightRetryTimerRef.current = {}
    highlightRetryCountRef.current = {}
  }, [])

  React.useEffect(() => {
    if (!isOpen || !contractId) return undefined
    let ignore = false
    setParsedTextSource('')
    setOcrLinesByPage({})
    setOcrWordsByPage({})
    dataProvider
      .getContractOcrLayout(contractId)
      .then((response) => {
        if (ignore) return
        setParsedTextSource(String(response?.parsed_text_source || ''))
        setOcrLinesByPage(extractOcrLinesByPage(response?.ocr_layout))
        setOcrWordsByPage(extractOcrWordsByPage(response?.ocr_layout))
      })
      .catch((error) => {
        if (ignore) return
        contractViewerDebug('ocr-layout-load-error', {
          contractId,
          error: String(error?.message || error || ''),
        })
      })
    return () => {
      ignore = true
    }
  }, [contractId, dataProvider, isOpen])

  React.useEffect(() => {
    if (!isOpen || !contractId) return undefined
    clearHighlightRetryTimers()
    setLoadingPreview(true)
    setPreviewBlobUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous)
      return ''
    })
    setFallbackText('')
    setFallbackTruncated(false)
    setSourceMissing(false)
    dataProvider
      .getContractPreviewBlob(contractId)
      .then((blob) => {
        if (!blob) throw new Error('No preview blob returned')
        const nextBlobUrl = URL.createObjectURL(blob)
        setPreviewBlobUrl((previous) => {
          if (previous) URL.revokeObjectURL(previous)
          return nextBlobUrl
        })
      })
      .catch((error) => {
        const details = String(error?.message || '')
        const isMissingSource =
          details.includes('404') ||
          details.toLowerCase().includes('not found') ||
          details.toLowerCase().includes('source file')

        if (!isMissingSource) {
          notify(`Unable to load contract viewer: ${error.message}`, {
            type: 'warning',
          })
          return
        }

        setSourceMissing(true)
        dataProvider
          .getContractPreviewFallback(contractId)
          .then((response) => {
            const textValue = String(response?.fallback_text || '')
            if (!textValue) {
              notify(
                'Contract source is missing and no extracted-text fallback is available',
                {
                  type: 'warning',
                }
              )
              return
            }
            setFallbackText(textValue)
            setFallbackTruncated(Boolean(response?.truncated))
          })
          .catch((fallbackError) => {
            notify(
              `Contract source is missing and fallback could not be loaded: ${fallbackError.message}`,
              {
                type: 'warning',
              }
            )
          })
      })
      .finally(() => {
        setLoadingPreview(false)
      })
    return undefined
  }, [clearHighlightRetryTimers, contractId, dataProvider, isOpen, notify])

  React.useEffect(
    () => () => {
      clearHighlightRetryTimers()
      setPreviewBlobUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous)
        return ''
      })
    },
    [clearHighlightRetryTimers]
  )

  React.useEffect(() => {
    if (!isOpen) return undefined
    const initialValue =
      normalizeEvidenceSearchText(extractedValue) ||
      normalizeEvidenceSearchText(evidenceText)
    setSearchTerm(initialValue)
    setResolvedAutoSearchKey('')
    setActiveMatchIndex(0)
    return undefined
  }, [evidenceText, extractedValue, isOpen])

  React.useEffect(() => {
    if (!previewBlobUrl && fallbackText) {
      clearHighlightRetryTimers()
      setLoadingPdf(false)
      setNumPages(1)
      setPageTextByPage({ 1: fallbackText })
      setHighlightCountsByPage({})
      pageRefs.current = {}
      return undefined
    }
    if (!previewBlobUrl) return undefined
    clearHighlightRetryTimers()
    setLoadingPdf(true)
    setNumPages(0)
    setPageTextByPage({})
    setHighlightCountsByPage({})
    pageRefs.current = {}
    return undefined
  }, [clearHighlightRetryTimers, previewBlobUrl, fallbackText])

  React.useEffect(() => {
    if (
      !isOpen ||
      !numPages ||
      resolvedAutoSearchKey === autoSearchKey ||
      Boolean(fallbackText)
    ) {
      return undefined
    }
    const extractedPageCount = Object.keys(pageTextByPage).length
    if (extractedPageCount < numPages) return undefined

    const extractedCandidate = normalizeEvidenceSearchText(extractedValue)
    const evidenceCandidate = normalizeEvidenceSearchText(evidenceText)
    const preferEvidenceCandidate = shouldTreatAsListSearch(
      String(evidenceText || '')
    )
    const candidates = [
      {
        label: 'Extracted Value',
        text: extractedCandidate,
        penalty: isLowSignalDerivedValue(extractedCandidate) ? 5000 : 0,
      },
      {
        label: 'Source Evidence',
        text: evidenceCandidate,
        penalty: isLikelyHeuristicText(evidenceCandidate) ? 1000 : 0,
      },
    ].filter(
      (candidate) =>
        candidate.text &&
        (!preferEvidenceCandidate || candidate.label === 'Source Evidence') &&
        (candidate.label !== 'Extracted Value' ||
          !isLowSignalDerivedValue(candidate.text))
    )

    if (candidates.length === 0) {
      setResolvedAutoSearchKey(autoSearchKey)
      return undefined
    }

    let winner = candidates[0]
    let winnerScore = -Infinity
    const useOcrSignal = Object.keys(ocrLinesByPage).length > 0
    candidates.forEach((candidate) => {
      const hits = useOcrSignal
        ? getTotalMatchesInOcrLayout(ocrLinesByPage, candidate.text)
        : getTotalMatchesInDocument(pageTextByPage, candidate.text)
      const score = hits * 100 - candidate.penalty
      if (
        score > winnerScore ||
        (score === winnerScore && candidate.label === 'Source Evidence')
      ) {
        winner = candidate
        winnerScore = score
      }
    })

    setSearchTerm(winner.text)
    setResolvedAutoSearchKey(autoSearchKey)
    setActiveMatchIndex(0)
    return undefined
  }, [
    autoSearchKey,
    evidenceText,
    extractedValue,
    isOpen,
    numPages,
    ocrLinesByPage,
    pageTextByPage,
    resolvedAutoSearchKey,
    fallbackText,
  ])

  React.useEffect(() => {
    if (!isOpen || !numPages || !pageHint) return undefined
    const targetPage = Math.max(1, Math.min(numPages, pageHint))
    scrollToPage(targetPage)
    return undefined
  }, [isOpen, numPages, pageHint, evidenceFieldName, scrollToPage])

  const isAutoSearchResolutionPending =
    isOpen &&
    Boolean(previewBlobUrl) &&
    !fallbackText &&
    resolvedAutoSearchKey !== autoSearchKey
  React.useEffect(() => {
    if (isAutoSearchResolutionPending) return
    stableEffectiveSearchTermRef.current = searchTerm
  }, [isAutoSearchResolutionPending, searchTerm])
  const effectiveSearchTerm = isAutoSearchResolutionPending
    ? stableEffectiveSearchTermRef.current
    : searchTerm
  const searchQueries = React.useMemo(
    () => resolveSearchQueriesForDocument(pageTextByPage, effectiveSearchTerm),
    [pageTextByPage, effectiveSearchTerm]
  )
  const hasOcrLayout = React.useMemo(
    () => Object.keys(ocrLinesByPage).length > 0,
    [ocrLinesByPage]
  )
  const textLayerPageCount = React.useMemo(
    () =>
      Object.values(pageTextByPage).reduce((count, pageText) => {
        const normalized = normalizeForSearchComparison(pageText).replace(
          /\s+/g,
          ''
        )
        return normalized.length > 0 ? count + 1 : count
      }, 0),
    [pageTextByPage]
  )
  const textLayerCharCount = React.useMemo(
    () =>
      Object.values(pageTextByPage).reduce((count, pageText) => {
        return (
          count +
          normalizeForSearchComparison(pageText).replace(/\s+/g, '').length
        )
      }, 0),
    [pageTextByPage]
  )
  const hasUsableTextLayer =
    numPages > 0 &&
    textLayerPageCount >= Math.max(1, Math.floor(numPages * 0.4)) &&
    textLayerCharCount >= 120
  const shouldUseOcrSearchMode =
    isOpen &&
    Boolean(previewBlobUrl) &&
    !fallbackText &&
    !loadingPdf &&
    hasOcrLayout &&
    !hasUsableTextLayer
  const inlineSearchPlaceholder = shouldUseOcrSearchMode
    ? 'Search in contract (OCR)'
    : 'Search in contract'
  const allowMultiQueryCandidates = shouldTreatAsListSearch(
    String(effectiveSearchTerm || '')
  )
  const shouldUseSingleQueryCandidate =
    !allowMultiQueryCandidates && searchQueries.length > 1
  const clampedPageHint = React.useMemo(() => {
    if (!pageHint || !Number.isFinite(pageHint) || pageHint <= 0) return null
    if (!numPages || numPages <= 0) return pageHint
    return Math.max(1, Math.min(numPages, pageHint))
  }, [numPages, pageHint])
  const normalizedSearchTerm = normalizeForSearchComparison(effectiveSearchTerm)
  const singleMatchTargetPage = React.useMemo(() => {
    if (!shouldUseSingleQueryCandidate) return null
    const pages = (
      shouldUseOcrSearchMode
        ? Object.keys(ocrLinesByPage)
        : Object.keys(pageTextByPage)
    )
      .map((pageKey) => Number.parseInt(pageKey, 10))
      .filter((pageNumber) => Number.isFinite(pageNumber) && pageNumber > 0)
    if (pages.length === 0) return clampedPageHint

    let bestPage: number | null = clampedPageHint
    let bestScore = Number.NEGATIVE_INFINITY

    pages.forEach((pageNumber) => {
      let score = 0
      searchQueries.forEach((queryCandidate, index) => {
        const normalizedQuery = normalizeForSearchComparison(queryCandidate)
        if (!normalizedQuery) return
        const queryWordCount = normalizedQuery.split(' ').filter(Boolean).length
        const hits = shouldUseOcrSearchMode
          ? (ocrLinesByPage[pageNumber] || []).reduce(
              (sum, line) => sum + countOccurrences(line.text, queryCandidate),
              0
            )
          : countOccurrences(pageTextByPage[pageNumber] || '', queryCandidate)
        if (hits <= 0) return

        const isExactWholeQuery = Boolean(
          normalizedSearchTerm && normalizedQuery === normalizedSearchTerm
        )
        const queryLooksLikeDate = DATE_LIKE_SINGLE_PATTERN.test(queryCandidate)
        const queryLooksLikeAmount = isAmountQueryCandidate(queryCandidate)
        const amountTokenCount = queryLooksLikeAmount
          ? countAmountLikeTokenOccurrences(queryCandidate)
          : 0
        let queryWeight = shouldUseOcrSearchMode ? 180 : 120
        if (isExactWholeQuery) {
          queryWeight = 1200
        } else if (queryLooksLikeDate) {
          queryWeight = shouldUseOcrSearchMode ? 620 : 480
        } else if (queryLooksLikeAmount) {
          if (amountTokenCount <= 1) {
            queryWeight = shouldUseOcrSearchMode ? 170 : 110
          } else {
            queryWeight = shouldUseOcrSearchMode ? 320 : 240
          }
        } else if (index === 0) {
          queryWeight = shouldUseOcrSearchMode ? 340 : 260
        }
        const queryQualityBoost = Math.min(
          240,
          queryWordCount * 26 + Math.min(180, normalizedQuery.length)
        )
        score += Math.min(hits, shouldUseOcrSearchMode ? 3 : 2) * queryWeight
        score += queryQualityBoost
      })
      if (clampedPageHint && pageNumber === clampedPageHint) {
        score += 1500
      }
      if (score > bestScore) {
        bestScore = score
        bestPage = pageNumber
      }
    })

    if (bestPage !== null) return bestPage
    return clampedPageHint
  }, [
    clampedPageHint,
    normalizedSearchTerm,
    ocrLinesByPage,
    pageTextByPage,
    searchQueries,
    shouldUseOcrSearchMode,
    shouldUseSingleQueryCandidate,
  ])
  const { matchesByPage: textMatchesByPage, matches: textMatches } =
    React.useMemo(() => {
      const nextMatchesByPage: Record<number, number> = {}
      const nextMatches: Array<ViewerMatch> = []
      if (searchQueries.length === 0) {
        return { matchesByPage: nextMatchesByPage, matches: nextMatches }
      }
      Object.keys(pageTextByPage).forEach((pageKey) => {
        const pageNumber = Number.parseInt(pageKey, 10)
        if (!Number.isFinite(pageNumber)) return
        if (
          shouldUseSingleQueryCandidate &&
          Number.isFinite(singleMatchTargetPage) &&
          pageNumber !== singleMatchTargetPage
        ) {
          return
        }
        const computedMatchCount = searchQueries.reduce(
          (sum, queryCandidate) =>
            sum + countOccurrences(pageTextByPage[pageNumber], queryCandidate),
          0
        )
        const highlightedCount = highlightCountsByPage[pageNumber]
        const hasHighlightedCount =
          Number.isFinite(highlightedCount) && highlightedCount >= 0
        if (shouldUseSingleQueryCandidate && !hasHighlightedCount) {
          return
        }
        const matchCount = hasHighlightedCount
          ? highlightedCount
          : computedMatchCount
        if (matchCount <= 0) return
        nextMatchesByPage[pageNumber] = matchCount
        let i = 0
        while (i < matchCount) {
          nextMatches.push({ pageNumber, indexOnPage: i })
          i += 1
        }
      })
      return { matchesByPage: nextMatchesByPage, matches: nextMatches }
    }, [
      highlightCountsByPage,
      pageTextByPage,
      searchQueries,
      shouldUseSingleQueryCandidate,
      singleMatchTargetPage,
    ])

  const {
    matchesByPage: ocrMatchesByPage,
    matches: ocrMatches,
    highlightedLineIndexesByPage: ocrHighlightedLineIndexesByPage,
  } = React.useMemo(() => {
    const nextMatchesByPage: Record<number, number> = {}
    const nextHighlightedLineIndexesByPage: Record<number, number[]> = {}
    const nextMatches: Array<ViewerMatch> = []
    if (!shouldUseOcrSearchMode || searchQueries.length === 0) {
      return {
        matchesByPage: nextMatchesByPage,
        matches: nextMatches,
        highlightedLineIndexesByPage: nextHighlightedLineIndexesByPage,
      }
    }

    const collectHits = (queryCandidate: string) => {
      const hits: Array<{ pageNumber: number; lineIndex: number }> = []
      Object.keys(ocrLinesByPage).forEach((pageKey) => {
        const pageNumber = Number.parseInt(pageKey, 10)
        if (!Number.isFinite(pageNumber)) return
        const lines = ocrLinesByPage[pageNumber] || []
        lines.forEach((line, lineIndex) => {
          if (countOccurrences(line.text, queryCandidate) > 0) {
            hits.push({ pageNumber, lineIndex })
          }
        })
      })
      return hits
    }

    const scorePageHint = (pageNumber: number) => {
      const preferredPage =
        (Number.isFinite(singleMatchTargetPage)
          ? singleMatchTargetPage
          : null) || clampedPageHint
      if (!preferredPage) return 0
      return pageNumber === preferredPage
        ? 1000
        : -Math.abs(pageNumber - preferredPage) * 120
    }

    if (shouldUseSingleQueryCandidate) {
      const normalizedSearchNoSpaces = normalizedSearchTerm.replace(/\s+/g, '')
      let bestSingle: OcrSingleCandidate | null = null

      const fullQueryHits = searchQueries
        .filter(
          (queryCandidate) =>
            normalizeForSearchComparison(queryCandidate).replace(/\s+/g, '') ===
            normalizedSearchNoSpaces
        )
        .flatMap((queryCandidate) =>
          collectHits(queryCandidate).map((hit) => ({ ...hit, queryCandidate }))
        )

      fullQueryHits.forEach((hit) => {
        const score = 2400 + scorePageHint(hit.pageNumber)
        if (!bestSingle || score > bestSingle.score) {
          bestSingle = {
            pageNumber: hit.pageNumber,
            lineIndexes: [hit.lineIndex],
            score,
            selectionReason: 'full-query-line',
            queryCandidate: hit.queryCandidate,
          }
        }
      })

      if (!bestSingle) {
        const labelQueryCandidates = searchQueries.filter((queryCandidate) => {
          return (
            !DATE_LIKE_SINGLE_PATTERN.test(queryCandidate) &&
            !isAmountQueryCandidate(queryCandidate)
          )
        })
        const valueQueryCandidates = searchQueries.filter((queryCandidate) => {
          return (
            DATE_LIKE_SINGLE_PATTERN.test(queryCandidate) ||
            isAmountQueryCandidate(queryCandidate)
          )
        })
        const labelHits = labelQueryCandidates.flatMap((queryCandidate) =>
          collectHits(queryCandidate)
        )
        const valueHits = valueQueryCandidates.flatMap((queryCandidate) =>
          collectHits(queryCandidate)
        )

        labelHits.forEach((labelHit) => {
          const labelLine =
            ocrLinesByPage[labelHit.pageNumber]?.[labelHit.lineIndex]
          if (!labelLine) return
          const labelCenter = getOcrLineCenter(labelLine)

          valueHits.forEach((valueHit) => {
            if (valueHit.pageNumber !== labelHit.pageNumber) return
            const valueLine =
              ocrLinesByPage[valueHit.pageNumber]?.[valueHit.lineIndex]
            if (!valueLine) return
            const valueCenter = getOcrLineCenter(valueLine)
            const verticalDistance = Math.abs(valueCenter.y - labelCenter.y)
            const horizontalOffset = valueCenter.x - labelCenter.x
            const score =
              1600 +
              scorePageHint(labelHit.pageNumber) -
              verticalDistance * 1800 -
              Math.abs(horizontalOffset) * 220 -
              (horizontalOffset < -0.02 ? 500 : 0)
            if (!bestSingle || score > bestSingle.score) {
              const lineIndexes = Array.from(
                new Set([labelHit.lineIndex, valueHit.lineIndex])
              ).sort((a, b) => a - b)
              bestSingle = {
                pageNumber: labelHit.pageNumber,
                lineIndexes,
                score,
                selectionReason: 'label-value-pair',
              }
            }
          })
        })
      }

      if (!bestSingle) {
        const rankedSearchQueries = [...searchQueries].sort((a, b) => {
          const aNormalized = normalizeForSearchComparison(a)
          const bNormalized = normalizeForSearchComparison(b)
          const aWordCount = aNormalized.split(' ').filter(Boolean).length
          const bWordCount = bNormalized.split(' ').filter(Boolean).length
          if (aWordCount !== bWordCount) return bWordCount - aWordCount
          return bNormalized.length - aNormalized.length
        })
        rankedSearchQueries.forEach((queryCandidate) => {
          const normalizedQuery = normalizeForSearchComparison(queryCandidate)
          const queryWordCount = normalizedQuery
            .split(' ')
            .filter(Boolean).length
          const queryQualityBoost = Math.min(
            420,
            queryWordCount * 40 + Math.min(220, normalizedQuery.length)
          )
          collectHits(queryCandidate).forEach((hit) => {
            const score =
              800 + queryQualityBoost + scorePageHint(hit.pageNumber)
            if (!bestSingle || score > bestSingle.score) {
              bestSingle = {
                pageNumber: hit.pageNumber,
                lineIndexes: [hit.lineIndex],
                score,
                selectionReason: 'ranked-query-hit',
                queryCandidate,
              }
            }
          })
        })
      }

      if (bestSingle) {
        const selected = bestSingle as OcrSingleCandidate
        const pageLines = ocrLinesByPage[selected.pageNumber] || []
        const expansionContext: OcrSectionExpansionContext = {
          searchQueries,
          searchTokens: getInformativeOcrSearchTokens(normalizedSearchTerm),
          phraseAnchors: buildOcrPhraseAnchors(normalizedSearchTerm),
          exactSearchQueries: buildOcrExactSearchQueries(effectiveSearchTerm),
        }
        const expandedSelection = expandOcrLineIndexesToSection(
          pageLines,
          selected.lineIndexes,
          expansionContext
        )
        const appliedLineIndexes =
          expandedSelection.lineIndexes.length > 0
            ? expandedSelection.lineIndexes
            : selected.lineIndexes
        contractViewerDebug('ocr-single-selection', {
          pageNumber: selected.pageNumber,
          seedLineIndexes: selected.lineIndexes,
          appliedLineIndexes,
          appliedLineCount: appliedLineIndexes.length,
          searchTokens: expansionContext.searchTokens,
          phraseAnchorCount: expansionContext.phraseAnchors.length,
          exactSearchQueryCount: expansionContext.exactSearchQueries.length,
          continuationLineIndexes: expandedSelection.continuationLineIndexes,
          queryContextLineIndexes: expandedSelection.queryContextLineIndexes,
          selectedQueryCandidate: selected.queryCandidate || null,
          selectionReason: selected.selectionReason || null,
        })
        nextMatchesByPage[selected.pageNumber] = 1
        nextHighlightedLineIndexesByPage[selected.pageNumber] =
          appliedLineIndexes
        nextMatches.push({
          pageNumber: selected.pageNumber,
          indexOnPage: 0,
          lineIndexes: appliedLineIndexes,
        })
      }

      return {
        matchesByPage: nextMatchesByPage,
        matches: nextMatches,
        highlightedLineIndexesByPage: nextHighlightedLineIndexesByPage,
      }
    }

    Object.keys(ocrLinesByPage).forEach((pageKey) => {
      const pageNumber = Number.parseInt(pageKey, 10)
      if (!Number.isFinite(pageNumber)) return
      const lineIndexSet = new Set<number>()
      searchQueries.forEach((queryCandidate) => {
        const hits = collectHits(queryCandidate)
        hits.forEach((hit) => {
          if (hit.pageNumber === pageNumber) {
            lineIndexSet.add(hit.lineIndex)
          }
        })
      })
      const lineIndexes = Array.from(lineIndexSet).sort((a, b) => a - b)
      if (lineIndexes.length === 0) return
      nextHighlightedLineIndexesByPage[pageNumber] = lineIndexes
      nextMatchesByPage[pageNumber] = lineIndexes.length
      lineIndexes.forEach((lineIndex, indexOnPage) => {
        nextMatches.push({
          pageNumber,
          indexOnPage,
          lineIndexes: [lineIndex],
        })
      })
    })

    return {
      matchesByPage: nextMatchesByPage,
      matches: nextMatches,
      highlightedLineIndexesByPage: nextHighlightedLineIndexesByPage,
    }
  }, [
    clampedPageHint,
    effectiveSearchTerm,
    normalizedSearchTerm,
    ocrLinesByPage,
    searchQueries,
    singleMatchTargetPage,
    shouldUseOcrSearchMode,
    shouldUseSingleQueryCandidate,
  ])

  const matchesByPage = shouldUseOcrSearchMode
    ? ocrMatchesByPage
    : textMatchesByPage
  const matches = shouldUseOcrSearchMode ? ocrMatches : textMatches

  React.useEffect(() => {
    if (!shouldUseOcrSearchMode) return
    clearHighlightRetryTimers()
    setHighlightCountsByPage({})
  }, [clearHighlightRetryTimers, shouldUseOcrSearchMode])

  React.useEffect(() => {
    contractViewerDebug('match-state', {
      contractId,
      searchTerm,
      effectiveSearchTerm,
      searchQueries,
      isAutoSearchResolutionPending,
      shouldUseOcrSearchMode,
      parsedTextSource,
      shouldUseSingleQueryCandidate,
      singleMatchTargetPage,
      matchesByPage,
      matchesLength: matches.length,
      activeMatchIndex,
      highlightCountsByPage,
    })
  }, [
    activeMatchIndex,
    contractId,
    highlightCountsByPage,
    matches.length,
    matchesByPage,
    searchQueries,
    searchTerm,
    effectiveSearchTerm,
    isAutoSearchResolutionPending,
    parsedTextSource,
    shouldUseOcrSearchMode,
    shouldUseSingleQueryCandidate,
    singleMatchTargetPage,
  ])

  const safeActiveMatchIndex =
    matches.length > 0
      ? Math.max(0, Math.min(matches.length - 1, activeMatchIndex))
      : 0
  const activeMatch = matches[safeActiveMatchIndex]
  const activeOcrLineIndexSet = React.useMemo(() => {
    if (!shouldUseOcrSearchMode) return new Set<number>()
    const lineIndexes = activeMatch?.lineIndexes || []
    return new Set(lineIndexes)
  }, [activeMatch, shouldUseOcrSearchMode])
  const ocrWordHighlightBoxesByPage = React.useMemo(() => {
    if (!shouldUseOcrSearchMode || searchQueries.length === 0) {
      return {} as Record<number, OcrWordHighlightBox[]>
    }
    const next: Record<number, OcrWordHighlightBox[]> = {}
    Object.keys(ocrHighlightedLineIndexesByPage).forEach((pageKey) => {
      const pageNumber = Number.parseInt(pageKey, 10)
      if (!Number.isFinite(pageNumber)) return
      const lineIndexes = ocrHighlightedLineIndexesByPage[pageNumber] || []
      if (lineIndexes.length === 0) return
      const lines = ocrLinesByPage[pageNumber] || []
      const words = ocrWordsByPage[pageNumber] || []
      if (lines.length === 0) return
      next[pageNumber] = buildOcrWordHighlightBoxes({
        lines,
        words,
        lineIndexes,
        searchTerm: effectiveSearchTerm,
        searchQueries,
      })
    })
    return next
  }, [
    effectiveSearchTerm,
    ocrHighlightedLineIndexesByPage,
    ocrLinesByPage,
    ocrWordsByPage,
    searchQueries,
    shouldUseOcrSearchMode,
  ])

  React.useEffect(() => {
    if (!isOpen || searchQueries.length === 0 || matches.length === 0) {
      return undefined
    }
    const target = matches[safeActiveMatchIndex]
    if (!target?.pageNumber) return undefined
    const timer = window.setTimeout(() => {
      scrollToPage(target.pageNumber)
    }, 120)
    return () => window.clearTimeout(timer)
  }, [isOpen, matches, safeActiveMatchIndex, scrollToPage, searchQueries])

  const jumpToMatch = (nextIndex: number) => {
    if (matches.length === 0) return
    const normalizedIndex =
      ((nextIndex % matches.length) + matches.length) % matches.length
    setActiveMatchIndex(normalizedIndex)
    const nextMatch = matches[normalizedIndex]
    if (nextMatch?.pageNumber) {
      scrollToPage(nextMatch.pageNumber)
    }
  }

  const clearExistingHighlights = React.useCallback((root: Element) => {
    const highlighted = root.querySelectorAll(
      '[data-contract-highlight="1"]'
    ) as unknown as HTMLElement[]
    highlighted.forEach((node) => {
      node.style.background = ''
      node.style.borderRadius = ''
      node.style.padding = ''
      node.removeAttribute('data-contract-highlight')
      node.removeAttribute('data-contract-highlight-kind')
    })
  }, [])

  const applyHighlightsToPage = React.useCallback(
    (pageNumber: number) => {
      const pageRoot = pageRefs.current[pageNumber]
      if (!pageRoot) return
      if (shouldUseOcrSearchMode) {
        clearExistingHighlights(pageRoot)
        setHighlightCountsByPage((previous) => {
          if (previous[pageNumber] === 0) return previous
          return { ...previous, [pageNumber]: 0 }
        })
        return
      }
      if (searchQueries.length === 0) {
        if (isAutoSearchResolutionPending) return
        clearExistingHighlights(pageRoot)
        return
      }

      if (
        shouldUseSingleQueryCandidate &&
        Number.isFinite(singleMatchTargetPage) &&
        pageNumber !== singleMatchTargetPage
      ) {
        clearExistingHighlights(pageRoot)
        setHighlightCountsByPage((previous) => {
          if (previous[pageNumber] === 0) return previous
          return { ...previous, [pageNumber]: 0 }
        })
        return
      }

      const allSpans = Array.from(
        pageRoot.querySelectorAll('.react-pdf__Page__textContent span')
      ) as HTMLElement[]
      const spans = allSpans.filter((span) => {
        const text = normalizeForSearchComparison(span.textContent || '')
        if (!text) return false
        const rect = span.getBoundingClientRect()
        if (
          !Number.isFinite(rect.width) ||
          !Number.isFinite(rect.height) ||
          rect.width <= 0 ||
          rect.height <= 0
        ) {
          return false
        }
        const style = window.getComputedStyle(span)
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false
        }
        const opacity = Number.parseFloat(style.opacity || '1')
        if (Number.isFinite(opacity) && opacity === 0) return false
        return true
      })
      const spansForMatching = spans.length > 0 ? spans : allSpans
      if (spansForMatching.length === 0) {
        const retries = highlightRetryCountRef.current[pageNumber] || 0
        if (retries < 20 && !highlightRetryTimerRef.current[pageNumber]) {
          highlightRetryCountRef.current[pageNumber] = retries + 1
          const timerId = window.setTimeout(() => {
            delete highlightRetryTimerRef.current[pageNumber]
            applyHighlightsToPage(pageNumber)
          }, 120)
          highlightRetryTimerRef.current[pageNumber] = timerId
        }
        return
      }
      if (highlightRetryTimerRef.current[pageNumber]) {
        window.clearTimeout(highlightRetryTimerRef.current[pageNumber])
        delete highlightRetryTimerRef.current[pageNumber]
      }
      highlightRetryCountRef.current[pageNumber] = 0
      clearExistingHighlights(pageRoot)

      const normalizedSpans = spansForMatching.map((span) =>
        normalizeForSearchComparison(span.textContent || '')
      )
      const buildCompactCharMap = (stripNumericPunctuation: boolean) => {
        const compactChars: string[] = []
        const compactCharToSpanIndex: number[] = []
        normalizedSpans.forEach((token, spanIndex) => {
          const compactToken = token.replace(/\s+/g, '')
          if (!compactToken) return
          const nextToken = stripNumericPunctuation
            ? stripNumericSeparators(compactToken)
            : compactToken
          if (!nextToken) return
          for (const ch of nextToken) {
            compactChars.push(ch)
            compactCharToSpanIndex.push(spanIndex)
          }
        })
        return {
          compactSpanText: compactChars.join(''),
          compactCharToSpanIndex,
        }
      }

      const buildDigitCharMap = () => {
        const compactChars: string[] = []
        const compactCharToSpanIndex: number[] = []
        normalizedSpans.forEach((token, spanIndex) => {
          const digitsOnly = token.replace(/\D/g, '')
          if (!digitsOnly) return
          for (const ch of digitsOnly) {
            compactChars.push(ch)
            compactCharToSpanIndex.push(spanIndex)
          }
        })
        return {
          compactSpanText: compactChars.join(''),
          compactCharToSpanIndex,
        }
      }

      const buildAlnumCharMap = () => {
        const compactChars: string[] = []
        const compactCharToSpanIndex: number[] = []
        normalizedSpans.forEach((token, spanIndex) => {
          const compactToken = token.replace(/\s+/g, '')
          const alnumToken = compactToken.replace(/[^a-z0-9]/g, '')
          if (!alnumToken) return
          for (const ch of alnumToken) {
            compactChars.push(ch)
            compactCharToSpanIndex.push(spanIndex)
          }
        })
        return {
          compactSpanText: compactChars.join(''),
          compactCharToSpanIndex,
        }
      }

      const highlightMatches = (
        compactSpanText: string,
        compactCharToSpanIndex: number[],
        targetQuery: string
      ) => {
        if (!compactSpanText || !targetQuery) return []
        const localMatches: Array<{
          startSpanIndex: number
          endSpanIndex: number
        }> = []
        let cursor = 0
        while (cursor < compactSpanText.length) {
          const startIndex = compactSpanText.indexOf(targetQuery, cursor)
          if (startIndex === -1) break
          const endIndex = startIndex + targetQuery.length - 1
          const startSpanIndex = compactCharToSpanIndex[startIndex]
          const endSpanIndex = compactCharToSpanIndex[endIndex]

          if (
            Number.isFinite(startSpanIndex) &&
            Number.isFinite(endSpanIndex) &&
            startSpanIndex >= 0 &&
            endSpanIndex >= startSpanIndex
          ) {
            localMatches.push({
              startSpanIndex,
              endSpanIndex,
            })
          }
          cursor = startIndex + Math.max(1, targetQuery.length)
        }
        return localMatches
      }

      const clearedHighlightsForThisPass = true

      const applySpanRange = (
        startSpanIndex: number,
        endSpanIndex: number,
        kind: 'label' | 'amount' = 'label'
      ) => {
        let markIndex = startSpanIndex
        while (markIndex <= endSpanIndex) {
          const node = spansForMatching[markIndex]
          if (!node) {
            markIndex += 1
            continue
          }
          node.style.background = 'rgba(245, 158, 11, 0.4)'
          node.style.borderRadius = '2px'
          node.style.padding = '0'
          node.setAttribute('data-contract-highlight', '1')
          node.setAttribute('data-contract-highlight-kind', kind)
          markIndex += 1
        }
      }

      const getRangeCenter = (
        startSpanIndex: number,
        endSpanIndex: number
      ): { x: number; y: number } | null => {
        let total = 0
        let totalY = 0
        let count = 0
        let index = startSpanIndex
        while (index <= endSpanIndex) {
          const rect = spansForMatching[index]?.getBoundingClientRect?.()
          if (
            rect &&
            Number.isFinite(rect.left) &&
            Number.isFinite(rect.width) &&
            Number.isFinite(rect.top) &&
            Number.isFinite(rect.height)
          ) {
            total += rect.left + rect.width / 2
            totalY += rect.top + rect.height / 2
            count += 1
          }
          index += 1
        }
        if (count === 0) return null
        return {
          x: total / count,
          y: totalY / count,
        }
      }

      const getRangeCenterX = (
        startSpanIndex: number,
        endSpanIndex: number
      ) => {
        const center = getRangeCenter(startSpanIndex, endSpanIndex)
        return center ? center.x : null
      }

      const getRangePreviewText = (
        startSpanIndex: number,
        endSpanIndex: number
      ) => {
        const tokens: string[] = []
        let index = startSpanIndex
        while (index <= endSpanIndex) {
          const token = normalizeForSearchComparison(
            spansForMatching[index]?.textContent
          )
          if (token) tokens.push(token)
          index += 1
        }
        return tokens.join(' ').replace(/\s+/g, ' ').trim().slice(0, 120)
      }

      const labelAnchorCenters: number[] = []
      const pickBestMatchByAnchors = (
        matchRanges: Array<{ startSpanIndex: number; endSpanIndex: number }>
      ) => {
        if (matchRanges.length === 0 || labelAnchorCenters.length === 0) {
          return null
        }
        let bestMatch = matchRanges[0]
        let bestDistance = Number.POSITIVE_INFINITY
        matchRanges.forEach((match) => {
          const centerX = getRangeCenterX(
            match.startSpanIndex,
            match.endSpanIndex
          )
          if (centerX === null) return
          const nearestAnchorDistance = labelAnchorCenters.reduce(
            (minDistance, anchorX) =>
              Math.min(minDistance, Math.abs(centerX - anchorX)),
            Number.POSITIVE_INFINITY
          )
          if (nearestAnchorDistance < bestDistance) {
            bestMatch = match
            bestDistance = nearestAnchorDistance
          }
        })
        return bestMatch
      }

      const pickBestMatchForPendingLabel = (
        matchRanges: Array<{ startSpanIndex: number; endSpanIndex: number }>,
        pendingLabelMatch: {
          startSpanIndex: number
          endSpanIndex: number
        } | null
      ) => {
        if (matchRanges.length === 0) return null
        if (!pendingLabelMatch) {
          return pickBestMatchByAnchors(matchRanges) || matchRanges[0]
        }

        const pendingCenter = getRangeCenter(
          pendingLabelMatch.startSpanIndex,
          pendingLabelMatch.endSpanIndex
        )
        if (!pendingCenter) {
          return pickBestMatchByAnchors(matchRanges) || matchRanges[0]
        }

        let bestMatch = matchRanges[0]
        let bestScore = Number.POSITIVE_INFINITY
        matchRanges.forEach((match) => {
          const center = getRangeCenter(
            match.startSpanIndex,
            match.endSpanIndex
          )
          if (!center) return

          const verticalDistance = Math.abs(center.y - pendingCenter.y)
          const horizontalOffset = center.x - pendingCenter.x
          const horizontalDistance = Math.abs(horizontalOffset)
          // In key-value rows we usually want the value on the same row and
          // to the right of the label.
          const leftSidePenalty = horizontalOffset < -8 ? 250 : 0
          const score =
            verticalDistance * 20 + horizontalDistance * 0.15 + leftSidePenalty
          if (score < bestScore) {
            bestMatch = match
            bestScore = score
          }
        })
        return bestMatch
      }

      const annotateSelectedMatch = (
        queryDebug: Record<string, unknown>,
        match: { startSpanIndex: number; endSpanIndex: number },
        selectionReason: string
      ) => {
        queryDebug.selectedMatch = {
          selectionReason,
          startSpanIndex: match.startSpanIndex,
          endSpanIndex: match.endSpanIndex,
          previewText: getRangePreviewText(
            match.startSpanIndex,
            match.endSpanIndex
          ),
        }
        const center = getRangeCenter(match.startSpanIndex, match.endSpanIndex)
        if (center) {
          queryDebug.selectedCenter = center
        }
        const sampleNode = spansForMatching[match.startSpanIndex]
        if (sampleNode) {
          const rect = sampleNode.getBoundingClientRect()
          const style = window.getComputedStyle(sampleNode)
          queryDebug.selectedNode = {
            opacity: style.opacity,
            visibility: style.visibility,
            display: style.display,
            rect: {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
            },
          }
        }
      }

      const baseCharMap = buildCompactCharMap(false)
      if (!baseCharMap.compactSpanText) return

      let usedNumericFallback = false
      let amountHighlightApplied = false
      const normalizedSearchTermNoSpaces = normalizeForSearchComparison(
        effectiveSearchTerm
      ).replace(/\s+/g, '')
      const amountFocusedSearch = searchQueries.some((queryCandidate) =>
        isAmountQueryCandidate(queryCandidate)
      )
      const queryDebugDetails: Array<Record<string, unknown>> = []
      let pendingSingleLabelMatch: {
        startSpanIndex: number
        endSpanIndex: number
      } | null = null
      let pendingSingleValueMatch: {
        startSpanIndex: number
        endSpanIndex: number
        kind: 'label' | 'amount'
      } | null = null
      let singleMatchApplied = false

      for (const queryCandidate of searchQueries) {
        const compactQuery = normalizeForSearchComparison(queryCandidate)
        if (!compactQuery) continue
        const compactQueryNoSpaces = compactQuery.replace(/\s+/g, '')
        if (!compactQueryNoSpaces) continue
        const queryLooksLikeAmount = isAmountQueryCandidate(queryCandidate)
        const queryLooksLikeDate = DATE_LIKE_SINGLE_PATTERN.test(queryCandidate)

        const queryDebug: Record<string, unknown> = {
          query: queryCandidate,
          queryLooksLikeAmount,
          queryLooksLikeDate,
          exactHitCount: 0,
          alnumHitCount: 0,
          digitHitCount: 0,
          numericHitCount: 0,
          applied: false,
        }

        const exactHits = highlightMatches(
          baseCharMap.compactSpanText,
          baseCharMap.compactCharToSpanIndex,
          compactQueryNoSpaces
        )
        queryDebug.exactHitCount = exactHits.length
        if (exactHits.length > 0) {
          if (shouldUseSingleQueryCandidate) {
            const bestSingleMatch = pickBestMatchForPendingLabel(
              exactHits,
              pendingSingleLabelMatch
            )
            if (!bestSingleMatch) {
              queryDebugDetails.push(queryDebug)
              continue
            }
            const isFullQueryCandidate =
              compactQueryNoSpaces === normalizedSearchTermNoSpaces
            if (isFullQueryCandidate) {
              applySpanRange(
                bestSingleMatch.startSpanIndex,
                bestSingleMatch.endSpanIndex,
                'label'
              )
              queryDebug.applied = true
              queryDebug.appliedMode = 'exact-single-full-applied'
              annotateSelectedMatch(
                queryDebug,
                bestSingleMatch,
                'single-full-query-applied'
              )
              queryDebugDetails.push(queryDebug)
              singleMatchApplied = true
              break
            }

            if (queryLooksLikeAmount || queryLooksLikeDate) {
              if (pendingSingleLabelMatch) {
                applySpanRange(
                  pendingSingleLabelMatch.startSpanIndex,
                  pendingSingleLabelMatch.endSpanIndex,
                  'label'
                )
                applySpanRange(
                  bestSingleMatch.startSpanIndex,
                  bestSingleMatch.endSpanIndex,
                  queryLooksLikeAmount ? 'amount' : 'label'
                )
                if (queryLooksLikeAmount) amountHighlightApplied = true
                if (!queryLooksLikeAmount) {
                  const centerX = getRangeCenterX(
                    bestSingleMatch.startSpanIndex,
                    bestSingleMatch.endSpanIndex
                  )
                  if (centerX !== null) labelAnchorCenters.push(centerX)
                }
                queryDebug.applied = true
                queryDebug.appliedMode = 'exact-single-label-plus-value'
                annotateSelectedMatch(
                  queryDebug,
                  bestSingleMatch,
                  'single-value-matched-to-pending-label'
                )
                queryDebugDetails.push(queryDebug)
                singleMatchApplied = true
                break
              }

              pendingSingleValueMatch = {
                startSpanIndex: bestSingleMatch.startSpanIndex,
                endSpanIndex: bestSingleMatch.endSpanIndex,
                kind: queryLooksLikeAmount ? 'amount' : 'label',
              }
              queryDebug.appliedMode = 'exact-single-pending-value'
              annotateSelectedMatch(
                queryDebug,
                bestSingleMatch,
                'single-value-pending-label'
              )
              queryDebugDetails.push(queryDebug)
              continue
            }

            if (!pendingSingleLabelMatch) {
              pendingSingleLabelMatch = {
                startSpanIndex: bestSingleMatch.startSpanIndex,
                endSpanIndex: bestSingleMatch.endSpanIndex,
              }
              const centerX = getRangeCenterX(
                bestSingleMatch.startSpanIndex,
                bestSingleMatch.endSpanIndex
              )
              if (centerX !== null) labelAnchorCenters.push(centerX)
              queryDebug.appliedMode = 'exact-single-pending-label'
              annotateSelectedMatch(
                queryDebug,
                bestSingleMatch,
                'single-pending-label'
              )
            } else {
              queryDebug.appliedMode = 'exact-single-ignored-label'
              annotateSelectedMatch(
                queryDebug,
                bestSingleMatch,
                'single-ignored-extra-label'
              )
            }
            queryDebugDetails.push(queryDebug)
            continue
          }

          if (
            (queryLooksLikeAmount || queryLooksLikeDate) &&
            exactHits.length > 1
          ) {
            const bestMatch = pickBestMatchByAnchors(exactHits)
            if (bestMatch) {
              applySpanRange(
                bestMatch.startSpanIndex,
                bestMatch.endSpanIndex,
                queryLooksLikeAmount ? 'amount' : 'label'
              )
              if (queryLooksLikeAmount) amountHighlightApplied = true
              queryDebug.applied = true
              queryDebug.appliedMode = 'exact-anchored'
              queryDebugDetails.push(queryDebug)
              if (shouldUseSingleQueryCandidate) break
              continue
            }
          }
          for (const match of exactHits) {
            applySpanRange(
              match.startSpanIndex,
              match.endSpanIndex,
              queryLooksLikeAmount ? 'amount' : 'label'
            )
            if (queryLooksLikeAmount) amountHighlightApplied = true
            if (!queryLooksLikeAmount) {
              const centerX = getRangeCenterX(
                match.startSpanIndex,
                match.endSpanIndex
              )
              if (centerX !== null) labelAnchorCenters.push(centerX)
            }
          }
          queryDebug.applied = true
          queryDebug.appliedMode = 'exact-all'
          queryDebugDetails.push(queryDebug)
          if (shouldUseSingleQueryCandidate) break
          continue
        }

        if (!hasNumericSeparatorPattern(compactQueryNoSpaces)) {
          queryDebugDetails.push(queryDebug)
          continue
        }

        if (queryLooksLikeDate || shouldUseSingleQueryCandidate) {
          const alnumQuery = compactQueryNoSpaces.replace(/[^a-z0-9]/g, '')
          if (alnumQuery) {
            const alnumCharMap = buildAlnumCharMap()
            const alnumHits = highlightMatches(
              alnumCharMap.compactSpanText,
              alnumCharMap.compactCharToSpanIndex,
              alnumQuery
            )
            queryDebug.alnumHitCount = alnumHits.length
            if (alnumHits.length > 0) {
              if (shouldUseSingleQueryCandidate) {
                const bestSingleAlnumMatch = pickBestMatchForPendingLabel(
                  alnumHits,
                  pendingSingleLabelMatch
                )
                if (!bestSingleAlnumMatch) {
                  queryDebugDetails.push(queryDebug)
                  continue
                }
                if (
                  pendingSingleLabelMatch &&
                  (queryLooksLikeDate || queryLooksLikeAmount)
                ) {
                  applySpanRange(
                    pendingSingleLabelMatch.startSpanIndex,
                    pendingSingleLabelMatch.endSpanIndex,
                    'label'
                  )
                  applySpanRange(
                    bestSingleAlnumMatch.startSpanIndex,
                    bestSingleAlnumMatch.endSpanIndex,
                    queryLooksLikeAmount ? 'amount' : 'label'
                  )
                  if (queryLooksLikeAmount) amountHighlightApplied = true
                  if (!queryLooksLikeAmount) {
                    const centerX = getRangeCenterX(
                      bestSingleAlnumMatch.startSpanIndex,
                      bestSingleAlnumMatch.endSpanIndex
                    )
                    if (centerX !== null) labelAnchorCenters.push(centerX)
                  }
                  queryDebug.applied = true
                  queryDebug.appliedMode = 'alnum-single-label-plus-value'
                  annotateSelectedMatch(
                    queryDebug,
                    bestSingleAlnumMatch,
                    'alnum-single-value-matched-to-pending-label'
                  )
                  queryDebugDetails.push(queryDebug)
                  singleMatchApplied = true
                  break
                }

                pendingSingleValueMatch = {
                  startSpanIndex: bestSingleAlnumMatch.startSpanIndex,
                  endSpanIndex: bestSingleAlnumMatch.endSpanIndex,
                  kind: queryLooksLikeAmount ? 'amount' : 'label',
                }
                queryDebug.appliedMode = 'alnum-single-pending-value'
                annotateSelectedMatch(
                  queryDebug,
                  bestSingleAlnumMatch,
                  'alnum-single-value-pending-label'
                )
                queryDebugDetails.push(queryDebug)
                continue
              }

              for (const match of alnumHits) {
                applySpanRange(
                  match.startSpanIndex,
                  match.endSpanIndex,
                  queryLooksLikeAmount ? 'amount' : 'label'
                )
                if (queryLooksLikeAmount) amountHighlightApplied = true
                if (!queryLooksLikeAmount) {
                  const centerX = getRangeCenterX(
                    match.startSpanIndex,
                    match.endSpanIndex
                  )
                  if (centerX !== null) labelAnchorCenters.push(centerX)
                }
              }
              queryDebug.applied = true
              queryDebug.appliedMode = 'alnum-all'
              queryDebugDetails.push(queryDebug)
              continue
            }
          }
        }

        if (queryLooksLikeAmount) {
          const digitQueries = buildAmountDigitCandidates(compactQueryNoSpaces)
          if (digitQueries.length > 0) {
            const digitCharMap = buildDigitCharMap()
            const digitHits = digitQueries.flatMap((digitQuery) =>
              highlightMatches(
                digitCharMap.compactSpanText,
                digitCharMap.compactCharToSpanIndex,
                digitQuery
              )
            )
            queryDebug.digitHitCount = digitHits.length
            if (digitHits.length > 0) {
              if (shouldUseSingleQueryCandidate) {
                const bestSingleDigitMatch = pickBestMatchForPendingLabel(
                  digitHits,
                  pendingSingleLabelMatch
                )
                if (!bestSingleDigitMatch) {
                  queryDebugDetails.push(queryDebug)
                  continue
                }
                if (pendingSingleLabelMatch) {
                  applySpanRange(
                    pendingSingleLabelMatch.startSpanIndex,
                    pendingSingleLabelMatch.endSpanIndex,
                    'label'
                  )
                  applySpanRange(
                    bestSingleDigitMatch.startSpanIndex,
                    bestSingleDigitMatch.endSpanIndex,
                    'amount'
                  )
                  amountHighlightApplied = true
                  queryDebug.applied = true
                  queryDebug.appliedMode = 'digit-single-label-plus-value'
                  annotateSelectedMatch(
                    queryDebug,
                    bestSingleDigitMatch,
                    'digit-single-value-matched-to-pending-label'
                  )
                  queryDebugDetails.push(queryDebug)
                  singleMatchApplied = true
                  break
                }

                pendingSingleValueMatch = {
                  startSpanIndex: bestSingleDigitMatch.startSpanIndex,
                  endSpanIndex: bestSingleDigitMatch.endSpanIndex,
                  kind: 'amount',
                }
                queryDebug.appliedMode = 'digit-single-pending-value'
                annotateSelectedMatch(
                  queryDebug,
                  bestSingleDigitMatch,
                  'digit-single-value-pending-label'
                )
                queryDebugDetails.push(queryDebug)
                continue
              }

              if (digitHits.length > 1) {
                const bestDigitMatch = pickBestMatchByAnchors(digitHits)
                if (bestDigitMatch) {
                  applySpanRange(
                    bestDigitMatch.startSpanIndex,
                    bestDigitMatch.endSpanIndex,
                    'amount'
                  )
                  amountHighlightApplied = true
                  queryDebug.applied = true
                  queryDebug.appliedMode = 'digit-anchored'
                  queryDebugDetails.push(queryDebug)
                  if (shouldUseSingleQueryCandidate) break
                  continue
                }
              }
              digitHits.forEach((match) =>
                applySpanRange(
                  match.startSpanIndex,
                  match.endSpanIndex,
                  'amount'
                )
              )
              amountHighlightApplied = true
              queryDebug.applied = true
              queryDebug.appliedMode = 'digit-all'
              queryDebugDetails.push(queryDebug)
              if (shouldUseSingleQueryCandidate) break
              continue
            }
          }
        }

        const numericQuery = stripNumericSeparators(compactQueryNoSpaces)
        if (!numericQuery) {
          queryDebugDetails.push(queryDebug)
          continue
        }

        if (!usedNumericFallback) {
          usedNumericFallback = true
        }
        const numericCharMap = buildCompactCharMap(true)
        const numericHits = highlightMatches(
          numericCharMap.compactSpanText,
          numericCharMap.compactCharToSpanIndex,
          numericQuery
        )
        queryDebug.numericHitCount = numericHits.length
        if (numericHits.length > 0 && shouldUseSingleQueryCandidate) {
          const bestSingleNumericMatch = pickBestMatchForPendingLabel(
            numericHits,
            pendingSingleLabelMatch
          )
          if (!bestSingleNumericMatch) {
            queryDebugDetails.push(queryDebug)
            continue
          }
          if (pendingSingleLabelMatch) {
            applySpanRange(
              pendingSingleLabelMatch.startSpanIndex,
              pendingSingleLabelMatch.endSpanIndex,
              'label'
            )
            applySpanRange(
              bestSingleNumericMatch.startSpanIndex,
              bestSingleNumericMatch.endSpanIndex,
              queryLooksLikeAmount ? 'amount' : 'label'
            )
            if (queryLooksLikeAmount) amountHighlightApplied = true
            if (!queryLooksLikeAmount) {
              const centerX = getRangeCenterX(
                bestSingleNumericMatch.startSpanIndex,
                bestSingleNumericMatch.endSpanIndex
              )
              if (centerX !== null) labelAnchorCenters.push(centerX)
            }
            queryDebug.applied = true
            queryDebug.appliedMode = 'numeric-single-label-plus-value'
            annotateSelectedMatch(
              queryDebug,
              bestSingleNumericMatch,
              'numeric-single-value-matched-to-pending-label'
            )
            queryDebugDetails.push(queryDebug)
            singleMatchApplied = true
            break
          }

          pendingSingleValueMatch = {
            startSpanIndex: bestSingleNumericMatch.startSpanIndex,
            endSpanIndex: bestSingleNumericMatch.endSpanIndex,
            kind: queryLooksLikeAmount ? 'amount' : 'label',
          }
          queryDebug.appliedMode = 'numeric-single-pending-value'
          annotateSelectedMatch(
            queryDebug,
            bestSingleNumericMatch,
            'numeric-single-value-pending-label'
          )
          queryDebugDetails.push(queryDebug)
          continue
        }

        if (
          (queryLooksLikeAmount || queryLooksLikeDate) &&
          numericHits.length > 1
        ) {
          const bestNumericMatch = pickBestMatchByAnchors(numericHits)
          if (bestNumericMatch) {
            applySpanRange(
              bestNumericMatch.startSpanIndex,
              bestNumericMatch.endSpanIndex,
              queryLooksLikeAmount ? 'amount' : 'label'
            )
            if (queryLooksLikeAmount) amountHighlightApplied = true
            queryDebug.applied = true
            queryDebug.appliedMode = 'numeric-anchored'
            queryDebugDetails.push(queryDebug)
            if (shouldUseSingleQueryCandidate) break
            continue
          }
        }
        for (const match of numericHits) {
          applySpanRange(
            match.startSpanIndex,
            match.endSpanIndex,
            queryLooksLikeAmount ? 'amount' : 'label'
          )
          if (queryLooksLikeAmount) amountHighlightApplied = true
          if (!queryLooksLikeAmount) {
            const centerX = getRangeCenterX(
              match.startSpanIndex,
              match.endSpanIndex
            )
            if (centerX !== null) labelAnchorCenters.push(centerX)
          }
        }
        if (numericHits.length > 0) {
          queryDebug.applied = true
          queryDebug.appliedMode = 'numeric-all'
        }
        queryDebugDetails.push(queryDebug)
        if (queryDebug.applied && shouldUseSingleQueryCandidate) break
      }

      if (
        shouldUseSingleQueryCandidate &&
        !singleMatchApplied &&
        pendingSingleLabelMatch &&
        pendingSingleValueMatch
      ) {
        applySpanRange(
          pendingSingleLabelMatch.startSpanIndex,
          pendingSingleLabelMatch.endSpanIndex,
          'label'
        )
        applySpanRange(
          pendingSingleValueMatch.startSpanIndex,
          pendingSingleValueMatch.endSpanIndex,
          pendingSingleValueMatch.kind
        )
        if (pendingSingleValueMatch.kind === 'amount') {
          amountHighlightApplied = true
        }
        singleMatchApplied = true
      }

      if (
        shouldUseSingleQueryCandidate &&
        !singleMatchApplied &&
        pendingSingleLabelMatch
      ) {
        applySpanRange(
          pendingSingleLabelMatch.startSpanIndex,
          pendingSingleLabelMatch.endSpanIndex,
          'label'
        )
        singleMatchApplied = true
      }

      if (
        shouldUseSingleQueryCandidate &&
        !singleMatchApplied &&
        pendingSingleValueMatch
      ) {
        applySpanRange(
          pendingSingleValueMatch.startSpanIndex,
          pendingSingleValueMatch.endSpanIndex,
          pendingSingleValueMatch.kind
        )
        if (pendingSingleValueMatch.kind === 'amount') {
          amountHighlightApplied = true
        }
        singleMatchApplied = true
      }

      if (!amountHighlightApplied && amountFocusedSearch) {
        const rawAmountTokens = extractAmountLikeTokens(searchTerm)
        if (rawAmountTokens.length > 0) {
          const digitCharMap = buildDigitCharMap()
          rawAmountTokens.forEach((amountToken) => {
            const normalizedAmount = normalizeForSearchComparison(amountToken)
            const digitQueries = buildAmountDigitCandidates(normalizedAmount)
            if (digitQueries.length === 0) return
            const amountHits = digitQueries.flatMap((digitQuery) =>
              highlightMatches(
                digitCharMap.compactSpanText,
                digitCharMap.compactCharToSpanIndex,
                digitQuery
              )
            )
            if (amountHits.length === 0) return
            if (amountHits.length > 1) {
              const bestAmountMatch = pickBestMatchByAnchors(amountHits)
              if (bestAmountMatch) {
                applySpanRange(
                  bestAmountMatch.startSpanIndex,
                  bestAmountMatch.endSpanIndex,
                  'amount'
                )
                amountHighlightApplied = true
                return
              }
            }
            amountHits.forEach((match) => {
              applySpanRange(match.startSpanIndex, match.endSpanIndex, 'amount')
              amountHighlightApplied = true
            })
          })
        }
      }

      let addedYearContextMatches = 0
      if (shouldUseSingleQueryCandidate && amountFocusedSearch) {
        const yearContextTokens = Array.from(
          new Set(
            normalizeForSearchComparison(effectiveSearchTerm).match(
              YEAR_HEADER_PATTERN
            ) || []
          )
        )
        const hasTotalAmountQuery = searchQueries.some((queryCandidate) => {
          const normalizedQuery = normalizeForSearchComparison(queryCandidate)
          return (
            normalizedQuery.includes('total') &&
            countAmountLikeTokenOccurrences(queryCandidate) >= 1
          )
        })
        if (yearContextTokens.length > 0 && hasTotalAmountQuery) {
          const highlightedIndexesBeforeContext: number[] = []
          spansForMatching.forEach((node, index) => {
            if (node.getAttribute('data-contract-highlight') === '1') {
              highlightedIndexesBeforeContext.push(index)
            }
          })
          if (highlightedIndexesBeforeContext.length > 0) {
            const anchorStart = highlightedIndexesBeforeContext[0]
            const anchorEnd =
              highlightedIndexesBeforeContext[
                highlightedIndexesBeforeContext.length - 1
              ]
            const maxContextDistance = 280
            yearContextTokens.forEach((yearToken) => {
              const compactYearToken = yearToken.replace(/\s+/g, '')
              if (!compactYearToken) return
              const yearMatches = highlightMatches(
                baseCharMap.compactSpanText,
                baseCharMap.compactCharToSpanIndex,
                compactYearToken
              )
              if (yearMatches.length === 0) return
              const bestYearMatch = yearMatches.reduce<{
                startSpanIndex: number
                endSpanIndex: number
                score: number
              } | null>((best, match) => {
                const center = (match.startSpanIndex + match.endSpanIndex) / 2
                const isAfterAnchor = center > anchorEnd
                const distance = isAfterAnchor
                  ? center - anchorEnd
                  : anchorStart - center
                if (distance > maxContextDistance) return best
                const score = distance + (isAfterAnchor ? 120 : 0)
                if (!best || score < best.score) {
                  return { ...match, score }
                }
                return best
              }, null)
              if (!bestYearMatch) return
              applySpanRange(
                bestYearMatch.startSpanIndex,
                bestYearMatch.endSpanIndex,
                'label'
              )
              addedYearContextMatches += 1
            })
          }
        }
      }

      const highlightedSpanIndexes: number[] = []
      const highlightedAmountSpanIndexes: number[] = []
      spansForMatching.forEach((node, index) => {
        if (node.getAttribute('data-contract-highlight') === '1') {
          highlightedSpanIndexes.push(index)
          if (node.getAttribute('data-contract-highlight-kind') === 'amount') {
            highlightedAmountSpanIndexes.push(index)
          }
        }
      })
      const countGroupedRanges = (indexes: number[]) => {
        let count = 0
        let previousIndex = -2
        indexes.forEach((index) => {
          if (index !== previousIndex + 1) count += 1
          previousIndex = index
        })
        return count
      }
      const groupedAmountCount = countGroupedRanges(
        highlightedAmountSpanIndexes
      )
      const groupedMatchCount = shouldUseSingleQueryCandidate
        ? highlightedSpanIndexes.length > 0
          ? 1
          : 0
        : groupedAmountCount > 0
        ? groupedAmountCount
        : countGroupedRanges(highlightedSpanIndexes)

      contractViewerDebug('page-highlight', {
        contractId,
        pageNumber,
        searchTerm,
        effectiveSearchTerm,
        searchQueries,
        isAutoSearchResolutionPending,
        allowMultiQueryCandidates,
        shouldUseSingleQueryCandidate,
        amountFocusedSearch,
        singleMatchApplied,
        pendingSingleLabelMatch: Boolean(pendingSingleLabelMatch),
        pendingSingleFullMatch: false,
        pendingSingleValueMatch: Boolean(pendingSingleValueMatch),
        queryDebugDetails,
        groupedMatchCount,
        groupedAmountCount,
        addedYearContextMatches,
        clearedHighlightsForThisPass,
      })

      setHighlightCountsByPage((previous) => {
        if (previous[pageNumber] === groupedMatchCount) return previous
        return { ...previous, [pageNumber]: groupedMatchCount }
      })
    },
    [
      allowMultiQueryCandidates,
      clearExistingHighlights,
      contractId,
      effectiveSearchTerm,
      isAutoSearchResolutionPending,
      searchQueries,
      searchTerm,
      shouldUseOcrSearchMode,
      shouldUseSingleQueryCandidate,
      singleMatchTargetPage,
    ]
  )

  React.useEffect(() => {
    Object.keys(pageRefs.current).forEach((pageKey) => {
      const pageNumber = Number.parseInt(pageKey, 10)
      if (!Number.isFinite(pageNumber) || pageNumber <= 0) return
      applyHighlightsToPage(pageNumber)
    })
  }, [applyHighlightsToPage])

  const externalViewerUrl = previewBlobUrl
    ? `${previewBlobUrl}${buildViewerFragment(
        searchTerm || extractedValue || evidenceText
      )}`
    : ''

  if (!isOpen) return null

  return (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: { xs: 2, xl: 0 },
        overflow: 'hidden',
        backgroundColor: '#fff',
        position: { xs: 'relative', xl: 'fixed' },
        top: { xl: 0 },
        right: { xl: 24 },
        bottom: { xl: 0 },
        width: { xs: '100%', xl: 'min(42vw, 760px)' },
        zIndex: { xl: 20 },
        height: { xs: 'calc(100vh - 48px)', xl: 'auto' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: '#f9fafb',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Contract Viewer
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Open in new tab" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    externalViewerUrl,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                disabled={!externalViewerUrl}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close viewer" arrow>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 0.5,
          alignItems: 'center',
          backgroundColor: '#fff',
        }}
      >
        <MuiTextField
          placeholder={inlineSearchPlaceholder}
          size="small"
          value={searchTerm}
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 30,
              height: 30,
            },
            '& .MuiInputBase-input': {
              py: 0,
              fontSize: '0.8rem',
            },
            '& .MuiInputAdornment-root': {
              mt: '0 !important',
              height: '100%',
              maxHeight: 'none',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': {
              fontSize: '0.95rem',
            },
          }}
          onChange={(event) => {
            setSearchTerm(event.target.value)
            setActiveMatchIndex(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {shouldUseOcrSearchMode && (
            <Chip
              size="small"
              label="OCR"
              sx={{
                height: 20,
                mr: 0.5,
                fontSize: '0.68rem',
                fontWeight: 600,
                backgroundColor: '#eef2ff',
                color: '#3730a3',
              }}
            />
          )}
          <Tooltip title="Previous match" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => jumpToMatch(safeActiveMatchIndex - 1)}
                disabled={matches.length === 0}
                sx={{ p: 0.35 }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ minWidth: 52, textAlign: 'center' }}
          >
            {matches.length > 0
              ? `${safeActiveMatchIndex + 1}/${matches.length}`
              : '0/0'}
          </Typography>
          <Tooltip title="Next match" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => jumpToMatch(safeActiveMatchIndex + 1)}
                disabled={matches.length === 0}
                sx={{ p: 0.35 }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom out" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setScale((prev) => Math.max(0.7, prev - 0.1))}
                sx={{ p: 0.35 }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom in" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setScale((prev) => Math.min(2.5, prev + 0.1))}
                sx={{ p: 0.35 }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          backgroundColor: '#f3f4f6',
          overflow: 'auto',
          p: 1,
        }}
      >
        {(loadingPreview || loadingPdf) && (
          <Typography variant="body2" color="textSecondary">
            Loading contract preview...
          </Typography>
        )}
        {!loadingPreview && !previewBlobUrl && fallbackText && (
          <Box
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Source PDF Unavailable
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              Showing stored extracted text fallback
              {sourceMissing ? ' (source file missing in object storage)' : ''}.
              {fallbackTruncated ? ' Output is truncated.' : ''}
            </Typography>
            <Box
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1,
                p: 1.25,
                maxHeight: '62vh',
                overflow: 'auto',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.78rem',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {fallbackText}
            </Box>
          </Box>
        )}
        {!loadingPreview && previewBlobUrl && (
          <Document
            key={`${contractId}-${previewBlobUrl}`}
            file={previewBlobUrl}
            loading=""
            onLoadSuccess={async (pdf) => {
              setNumPages(pdf.numPages || 0)
              try {
                const pageNumbers = Array.from(
                  { length: pdf.numPages || 0 },
                  (_, index) => index + 1
                )
                const entries = await Promise.all(
                  pageNumbers.map(async (pageNumber) => {
                    try {
                      const page = await pdf.getPage(pageNumber)
                      const textContent = await page.getTextContent()
                      const combined = textContent.items
                        .map((item) => ('str' in item ? item.str : ''))
                        .join(' ')
                      return [pageNumber, combined] as const
                    } catch (error) {
                      return [pageNumber, ''] as const
                    }
                  })
                )
                const nextPageText: Record<number, string> = {}
                entries.forEach(([pageNumber, textValue]) => {
                  nextPageText[pageNumber] = textValue
                })
                setPageTextByPage(nextPageText)
              } finally {
                setLoadingPdf(false)
              }
            }}
            onLoadError={(error) => {
              setLoadingPdf(false)
              notify(`Unable to render contract PDF: ${error.message}`, {
                type: 'warning',
              })
            }}
            onSourceError={(error) => {
              setLoadingPdf(false)
              notify(`Unable to read contract source: ${error.message}`, {
                type: 'warning',
              })
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Array.from({ length: numPages }, (_, index) => {
                const pageNumber = index + 1
                const pageMatchCount = matchesByPage[pageNumber] || 0
                const ocrLines = ocrLinesByPage[pageNumber] || []
                const highlightedOcrLineIndexes = shouldUseOcrSearchMode
                  ? ocrHighlightedLineIndexesByPage[pageNumber] || []
                  : []
                const highlightedOcrWordBoxes = shouldUseOcrSearchMode
                  ? ocrWordHighlightBoxesByPage[pageNumber] || []
                  : []
                const isActivePage =
                  Boolean(activeMatch) && activeMatch.pageNumber === pageNumber
                return (
                  <Box
                    key={`pdf-page-${pageNumber}`}
                    ref={(node) => {
                      pageRefs.current[pageNumber] =
                        node as HTMLDivElement | null
                    }}
                    sx={{
                      border: isActivePage
                        ? '2px solid #f59e0b'
                        : '1px solid #e5e7eb',
                      borderRadius: 1,
                      backgroundColor: '#fff',
                      p: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Page {pageNumber}
                      </Typography>
                      {pageMatchCount > 0 && (
                        <Chip
                          size="small"
                          label={`${pageMatchCount} match${
                            pageMatchCount === 1 ? '' : 'es'
                          }`}
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderAnnotationLayer
                        renderTextLayer
                        loading=""
                        onRenderSuccess={() => {
                          if (shouldUseOcrSearchMode) return
                          applyHighlightsToPage(pageNumber)
                        }}
                        onRenderTextLayerSuccess={() => {
                          if (shouldUseOcrSearchMode) return
                          applyHighlightsToPage(pageNumber)
                        }}
                      />
                      {shouldUseOcrSearchMode &&
                        highlightedOcrLineIndexes.length > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              pointerEvents: 'none',
                              zIndex: 3,
                              mixBlendMode: 'multiply',
                            }}
                          >
                            {highlightedOcrWordBoxes.length > 0
                              ? highlightedOcrWordBoxes.map(
                                  (wordBox, boxIndex) => {
                                    const isActiveLine =
                                      isActivePage &&
                                      activeOcrLineIndexSet.has(
                                        wordBox.lineIndex
                                      )
                                    return (
                                      <Box
                                        key={`ocr-word-${pageNumber}-${wordBox.lineIndex}-${boxIndex}`}
                                        sx={{
                                          position: 'absolute',
                                          left: `${wordBox.bbox.left * 100}%`,
                                          top: `${wordBox.bbox.top * 100}%`,
                                          width: `${wordBox.bbox.width * 100}%`,
                                          height: `${
                                            wordBox.bbox.height * 100
                                          }%`,
                                          backgroundColor: isActiveLine
                                            ? 'rgba(253, 224, 71, 0.32)'
                                            : 'rgba(253, 224, 71, 0.22)',
                                          border: 'none',
                                          borderRadius: '3px',
                                        }}
                                      />
                                    )
                                  }
                                )
                              : highlightedOcrLineIndexes.map((lineIndex) => {
                                  const line = ocrLines[lineIndex]
                                  if (!line) return null
                                  const isActiveLine =
                                    isActivePage &&
                                    activeOcrLineIndexSet.has(lineIndex)
                                  return (
                                    <Box
                                      key={`ocr-line-${pageNumber}-${lineIndex}`}
                                      sx={{
                                        position: 'absolute',
                                        left: `${line.bbox.left * 100}%`,
                                        top: `${line.bbox.top * 100}%`,
                                        width: `${line.bbox.width * 100}%`,
                                        height: `${line.bbox.height * 100}%`,
                                        backgroundColor: isActiveLine
                                          ? 'rgba(253, 224, 71, 0.3)'
                                          : 'rgba(253, 224, 71, 0.2)',
                                        border: 'none',
                                        borderRadius: '3px',
                                      }}
                                    />
                                  )
                                })}
                          </Box>
                        )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Document>
        )}
      </Box>
    </Box>
  )
}

const ConfidenceValue = ({ record, source }) => {
  const fieldName =
    source && source.includes('.') ? source.split('.').pop() : source
  const score = record && fieldName ? record[fieldName] : 0
  const confidence = Math.round((score || 0) * 100)
  const color = getConfidenceColor(confidence)

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {confidence}%
      </Typography>
    </Box>
  )
}

const CreateVendorButton = ({ contractId, supplierName, currentVendorId }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)

  // Don't show if already linked to a vendor
  if (currentVendorId || !supplierName || supplierName === 'None') return null

  const handleCreateVendor = () => {
    setLoading(true)
    dataProvider
      .createVendorFromContract(contractId, supplierName)
      .then((response) => {
        notify(response.message, { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(`Error creating vendor: ${error.message}`, { type: 'warning' })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={handleCreateVendor}
      disabled={loading}
      sx={{ mt: 1 }}
    >
      {loading ? 'Creating...' : 'Create Vendor from This Name'}
    </Button>
  )
}

const MetadataIterator = ({ onSelectEvidence, activeFieldName, ...props }) => {
  const normalizedActiveFieldName =
    normalizeForSearchComparison(activeFieldName)
  return (
    <SimpleFormIterator {...props} disableAdd disableRemove>
      <FormDataConsumer>
        {({ getSource, scopedFormData, formData }) => {
          const fieldValueSource = getSource
            ? getSource('field_value')
            : undefined
          const confidenceSource = getSource
            ? getSource('confidence_score')
            : undefined
          const evidenceSource = getSource
            ? getSource('evidence_quote')
            : undefined
          const verifiedSource = getSource
            ? getSource('is_verified')
            : undefined
          const evidenceText = scopedFormData?.evidence_quote || ''
          const isNonDocumentEvidenceField = NON_DOCUMENT_EVIDENCE_FIELDS.has(
            String(scopedFormData?.field_name || '').trim()
          )
          const canViewEvidence =
            Boolean(String(evidenceText).trim()) && !isNonDocumentEvidenceField
          const handleViewEvidence = () => {
            if (!canViewEvidence || !onSelectEvidence) return
            onSelectEvidence({
              fieldName: scopedFormData?.field_name || '',
              evidenceText,
              extractedValue: scopedFormData?.field_value || '',
            })
          }

          const isMandatory = isFieldMandatory(scopedFormData?.field_name)
          const isSupplierName = scopedFormData?.field_name === 'Supplier Name'
          const isFocusedField =
            Boolean(normalizedActiveFieldName) &&
            normalizeForSearchComparison(scopedFormData?.field_name) ===
              normalizedActiveFieldName
          let metadataBorder = '1px solid #e5e7eb'
          let metadataBorderLeft = '1px solid #e5e7eb'
          let metadataBackground = '#ffffff'
          let metadataShadow = 'none'

          if (isMandatory) {
            metadataBorder = '1px solid #f59e0b'
            metadataBorderLeft = '4px solid #d97706'
            metadataBackground = '#fffbeb'
          }

          if (isFocusedField) {
            metadataBorder = '1px solid #2563eb'
            metadataBorderLeft = '4px solid #1d4ed8'
            metadataBackground = '#eff6ff'
            metadataShadow = '0 0 0 1px rgba(37, 99, 235, 0.18)'
          }

          return (
            <Box
              sx={{
                border: metadataBorder,
                borderLeft: metadataBorderLeft,
                borderRadius: 2,
                mb: 2.5,
                px: 2,
                py: 2,
                width: '100%',
                backgroundColor: metadataBackground,
                boxShadow: metadataShadow,
                transition:
                  'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
              }}
            >
              <Grid
                container
                spacing={3}
                alignItems="start"
                sx={{ width: '100%' }}
              >
                <Grid item xs={12} lg={3}>
                  <Labeled
                    label={<MetadataFieldLabel field={scopedFormData} />}
                    sx={{ '& .MuiFormLabel-root': { mb: 1 } }}
                  >
                    <Box sx={{ pt: 0.5 }}>
                      {confidenceSource && (
                        <ConfidenceValue
                          source={confidenceSource}
                          record={scopedFormData}
                        />
                      )}
                    </Box>
                  </Labeled>
                </Grid>
                <Grid item xs={12} lg={7}>
                  {fieldValueSource && (
                    <>
                      <TextInput
                        source={fieldValueSource}
                        label="Extracted Value"
                        multiline
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            minHeight: '56px',
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                      {isSupplierName && scopedFormData?.is_verified && (
                        <CreateVendorButton
                          contractId={formData.id}
                          supplierName={scopedFormData?.field_value}
                          currentVendorId={formData.vendor_id}
                        />
                      )}
                      {evidenceSource && (
                        <Box sx={{ mt: 1.5 }}>
                          <MuiTextField
                            label="Source Evidence"
                            multiline
                            fullWidth
                            value={evidenceText}
                            inputProps={{ readOnly: true }}
                            onClick={handleViewEvidence}
                            sx={{
                              '& .MuiInputBase-root': {
                                minHeight: '56px',
                                backgroundColor: '#f9fafb',
                                cursor: canViewEvidence ? 'pointer' : 'default',
                              },
                            }}
                          />
                          {canViewEvidence && (
                            <Box
                              sx={{
                                mt: 1,
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleViewEvidence}
                              >
                                View In Contract
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                      mt: { xs: 0.5, lg: 2.5 },
                      display: 'flex',
                      justifyContent: { xs: 'flex-start', lg: 'center' },
                      alignItems: 'center',
                      minHeight: 56,
                    }}
                  >
                    {verifiedSource && (
                      <BooleanInput source={verifiedSource} label="Verify" />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )
        }}
      </FormDataConsumer>
    </SimpleFormIterator>
  )
}

const ContractMetadataPanel = ({ onSelectEvidence, activeFieldName }) => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [retryLoading, setRetryLoading] = React.useState(false)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const metadataFields = React.useMemo(
    () => record?.metadata_fields ?? [],
    [record?.metadata_fields]
  )
  const hasMetadata = metadataFields.length > 0
  const terminalNoDataFailure =
    record?.status === 'extraction_failed' &&
    typeof record?.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))

  React.useEffect(() => {
    if (hasMetadata) {
      let needsSorting = false
      let i = 1
      while (i < metadataFields.length) {
        if (
          getMetadataSortRank(metadataFields[i - 1]) >
          getMetadataSortRank(metadataFields[i])
        ) {
          needsSorting = true
          break
        }
        i += 1
      }

      if (needsSorting) {
        metadataFields.sort(
          (a, b) => getMetadataSortRank(a) - getMetadataSortRank(b)
        )
      }
    }
  }, [hasMetadata, metadataFields])

  if (!record) return null

  const handleRetryExtraction = () => {
    setRetryLoading(true)
    const forceRetry = record.status === 'processing'
    dataProvider
      .retryExtraction(record.id, forceRetry)
      .then(() => {
        notify('AI extraction retried', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setRetryLoading(false)
      })
  }

  const handlePreview = () => {
    if (!record?.id || previewLoading) return
    setPreviewLoading(true)
    dataProvider
      .getContractPreviewBlob(record.id)
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, '_blank', 'noopener,noreferrer')
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
      })
      .catch((error) => {
        notify(`Error opening preview: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  return (
    <Box sx={{ mt: 4, mb: 2, overflow: 'hidden', width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1,
          mb: 2,
          fontWeight: 700,
        }}
      >
        Extracted Metadata
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mb: 1.5 }}
      >
        Core Review fields are part of the standard checklist. Some may be
        legitimately not present for this contract type.
      </Typography>
      {!hasMetadata && (
        <Box
          sx={{
            py: 2,
            px: 2.5,
            mb: 2,
            border: '1px solid #fde68a',
            borderRadius: 1.5,
            backgroundColor: '#fffbeb',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#78350f' }}
          >
            No fields were extracted from this document.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.75 }}>
            {terminalNoDataFailure
              ? 'This file is either an unsupported PDF portfolio or has no usable metadata values. Retrying the same file is unlikely to help. Extract inner documents or save as a standard PDF, then re-upload.'
              : 'Retry AI extraction to repopulate metadata fields before review.'}
          </Typography>
          {terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? 'Opening...' : 'Preview PDF'}
              </Button>
            </Box>
          )}
          {!terminalNoDataFailure && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  <ArrowPathIcon
                    className={`h-4 w-4 ${retryLoading ? 'animate-spin' : ''}`}
                  />
                }
                onClick={handleRetryExtraction}
                disabled={retryLoading}
              >
                Retry AI Extraction
              </Button>
            </Box>
          )}
        </Box>
      )}
      <ArrayInput
        source="metadata_fields"
        label=""
        sx={{
          '& .MuiFormGroup-root': {
            width: '100%',
            px: 0,
            pt: 1,
          },
          '& .RaSimpleFormIterator-root': { width: '100%' },
          '& .RaSimpleFormIterator-list': { width: '100%' },
          '& .RaSimpleFormIterator-form': { width: '100%' },
          '& .RaSimpleFormIterator-line': {
            border: 'none',
            width: '100%',
            maxWidth: 'none',
            display: 'block',
          },
          '& .RaSimpleFormIterator-index': { display: 'none' },
          '& .RaSimpleFormIterator-action': { display: 'none' },
        }}
      >
        <MetadataIterator
          onSelectEvidence={onSelectEvidence}
          activeFieldName={activeFieldName}
        />
      </ArrayInput>

      <FormDataConsumer>
        {({ formData }) => {
          if (formData.status !== 'extraction_failed') return null
          return (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#fef2f2',
                border: '1px solid #fee2e2',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="error" gutterBottom>
                Extraction Error
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.extraction_error_message ? (
                  <span>{formData.extraction_error_message}</span>
                ) : (
                  <span>
                    The AI extraction failed. Common causes are unreadable PDF
                    text, no usable fields extracted, or provider/rate-limit
                    issues. You can retry using the &quot;Retry AI&quot; button
                    in the header.
                  </span>
                )}
              </Typography>
            </Box>
          )
        }}
      </FormDataConsumer>
    </Box>
  )
}

const RELATIONSHIP_TYPE_CHOICES = [
  'SOW_OF',
  'AMENDMENT_TO',
  'RENEWAL_OF',
  'SUPPLEMENT_TO',
  'LINKED_TO',
]

type ContractRelationshipRow = {
  id: number
  relationship_type: string
  direction: 'parent' | 'child'
  related_contract_id: number
  related_contract_filename: string
  related_contract_status: string
  created_at: string
}

type ContractLinkOption = {
  id: number
  filename: string
  status: string
  contract_type?: string | null
  supplier_name?: string | null
  contract_ref?: string | null
  effective_date?: string | null
  contract_value?: string | null
}

type ContractRelationshipSuggestion = {
  related_contract_id: number
  related_contract_filename: string
  related_contract_status: string
  related_contract_type?: string | null
  score: number
  reasons: string[]
}

const getMetadataFieldValue = (record, fieldName) => {
  if (!record || !Array.isArray(record.metadata_fields)) return ''
  const row = record.metadata_fields.find(
    (item) => item?.field_name === fieldName
  )
  if (!row?.field_value) return ''
  const value = String(row.field_value).trim()
  if (!value || value === 'None' || value === 'null') return ''
  return value
}

const ContractRelationshipsPanel = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const [rows, setRows] = React.useState<ContractRelationshipRow[]>([])
  const [loading, setLoading] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<number | null>(null)
  const [relatedId, setRelatedId] = React.useState('')
  const [selectedLink, setSelectedLink] =
    React.useState<ContractLinkOption | null>(null)
  const [linkOptions, setLinkOptions] = React.useState<ContractLinkOption[]>([])
  const [linkQuery, setLinkQuery] = React.useState('')
  const [loadingOptions, setLoadingOptions] = React.useState(false)
  const [missingParentSuggestions, setMissingParentSuggestions] =
    React.useState<ContractRelationshipSuggestion[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false)
  const [linkingSuggestionId, setLinkingSuggestionId] = React.useState<
    number | null
  >(null)
  const [relationshipType, setRelationshipType] = React.useState('SOW_OF')
  const [role, setRole] = React.useState('child')
  const recordId = record?.id

  const loadRelationships = React.useCallback(() => {
    if (!recordId) return
    setLoading(true)
    dataProvider
      .getContractRelationships(recordId)
      .then((result) => {
        setRows(
          Array.isArray(result) ? (result as ContractRelationshipRow[]) : []
        )
      })
      .catch((error) => {
        notify(`Error loading relationships: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dataProvider, notify, recordId])

  React.useEffect(() => {
    loadRelationships()
  }, [loadRelationships])

  const parentAgreementReference = getMetadataFieldValue(
    record,
    'Parent Agreement'
  )
  const supplierName = getMetadataFieldValue(record, 'Supplier Name')
  const hasParentLink = rows.some((row) => row.direction === 'parent')
  const showMissingParentAlert =
    Boolean(parentAgreementReference) && !hasParentLink

  React.useEffect(() => {
    if (!recordId) return undefined
    const timer = window.setTimeout(() => {
      setLoadingOptions(true)
      dataProvider
        .getList('contracts', {
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'updated_at', order: 'DESC' },
          filter: linkQuery.trim()
            ? {
                q: linkQuery.trim(),
                ...(supplierName ? { supplier_name: supplierName } : {}),
              }
            : supplierName
            ? { supplier_name: supplierName }
            : {},
        })
        .then((response) => {
          const options = (response?.data || [])
            .filter((row) => row?.id && row.id !== recordId)
            .map((row) => {
              const extractMetadata = (fieldName) => {
                if (!Array.isArray(row.metadata_fields)) return null
                const field = row.metadata_fields.find(
                  (f) => f?.field_name === fieldName
                )
                return field?.field_value || null
              }

              const optionSupplierName = extractMetadata('Supplier Name')
              const optionContractRef = extractMetadata('Contract Reference')
              const optionEffectiveDate = extractMetadata('Effective Date')
              const optionContractValue = extractMetadata('Contract Value')

              return {
                id: row.id,
                filename: row.filename || `Contract ${row.id}`,
                status: row.status || 'unknown',
                contract_type: row.contract_type || null,
                supplier_name: optionSupplierName,
                contract_ref: optionContractRef,
                effective_date: optionEffectiveDate,
                contract_value: optionContractValue,
              }
            })
          setLinkOptions(options)
        })
        .catch(() => {
          setLinkOptions([])
        })
        .finally(() => {
          setLoadingOptions(false)
        })
    }, 250)

    return () => {
      window.clearTimeout(timer)
    }
  }, [dataProvider, linkQuery, recordId, supplierName])

  const handleCreate = () => {
    const selectedId = selectedLink?.id
    const parsedId = Number.parseInt(String(selectedId || relatedId), 10)
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      notify('Select a related contract or enter a valid ID', {
        type: 'warning',
      })
      return
    }
    setSubmitting(true)
    dataProvider
      .createContractRelationship(record.id, {
        related_contract_id: parsedId,
        relationship_type: relationshipType,
        current_contract_role: role,
      })
      .then(() => {
        notify('Relationship saved', { type: 'success' })
        setRelatedId('')
        setSelectedLink(null)
        setLinkQuery('')
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error saving relationship: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const createParentLinkFromSuggestion = (suggestedContractId: number) => {
    setLinkingSuggestionId(suggestedContractId)
    dataProvider
      .createContractRelationship(recordId, {
        related_contract_id: suggestedContractId,
        relationship_type: 'SOW_OF',
        current_contract_role: 'child',
      })
      .then(() => {
        notify('Parent link created', { type: 'success' })
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error linking parent: ${error.message}`, { type: 'warning' })
      })
      .finally(() => {
        setLinkingSuggestionId(null)
      })
  }

  const handleDelete = (relationshipId) => {
    setDeletingId(relationshipId)
    dataProvider
      .deleteContractRelationship(record.id, relationshipId)
      .then(() => {
        notify('Relationship removed', { type: 'info' })
        loadRelationships()
        refresh()
      })
      .catch((error) => {
        notify(`Error removing relationship: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setDeletingId(null)
      })
  }

  React.useEffect(() => {
    if (!showMissingParentAlert) {
      setMissingParentSuggestions([])
      return undefined
    }
    const queryText = parentAgreementReference.trim()
    if (!queryText) {
      setMissingParentSuggestions([])
      return undefined
    }

    setLoadingSuggestions(true)
    dataProvider
      .getContractRelationshipSuggestions(recordId, 5)
      .then((response) => {
        const options = Array.isArray(response)
          ? (response as ContractRelationshipSuggestion[])
          : []
        setMissingParentSuggestions(options)
      })
      .catch(() => {
        setMissingParentSuggestions([])
      })
      .finally(() => {
        setLoadingSuggestions(false)
      })
    return undefined
  }, [dataProvider, parentAgreementReference, recordId, showMissingParentAlert])

  if (!record || !recordId) return null

  const parentRows = rows.filter((row) => row.direction === 'parent')
  const childRows = rows.filter((row) => row.direction === 'child')

  return (
    <Box sx={{ mt: 3, mb: 2, width: '100%', maxWidth: 'none' }}>
      <Typography
        variant="h6"
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 1,
          mb: 2,
          fontWeight: 700,
        }}
      >
        Contract Relationships
      </Typography>
      {showMissingParentAlert && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            border: '1px solid #f59e0b',
            borderRadius: 1.5,
            backgroundColor: '#fffbeb',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#92400e' }}
          >
            Missing Parent Link
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Parent Agreement extracted as: &quot;{parentAgreementReference}
            &quot; but this contract has no linked parent.
          </Typography>
          <Box sx={{ mt: 1 }}>
            {loadingSuggestions && (
              <Typography variant="caption" color="textSecondary">
                Searching likely parent contracts...
              </Typography>
            )}
            {!loadingSuggestions &&
              missingParentSuggestions.map((option) => (
                <Box
                  key={option.related_contract_id}
                  sx={{
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    borderTop: '1px solid #fde68a',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      #{option.related_contract_id}{' '}
                      {option.related_contract_filename}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.related_contract_type || 'Unspecified type'} •{' '}
                      {option.related_contract_status || 'unknown'} • score{' '}
                      {option.score}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      createParentLinkFromSuggestion(option.related_contract_id)
                    }
                    disabled={
                      linkingSuggestionId === option.related_contract_id
                    }
                  >
                    {linkingSuggestionId === option.related_contract_id
                      ? 'Linking...'
                      : 'Link'}
                  </Button>
                </Box>
              ))}
            {!loadingSuggestions && missingParentSuggestions.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No likely parent contract found. Use search below to link
                manually.
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Stack spacing={1.2}>
        {loading && (
          <Typography variant="body2" color="textSecondary">
            Loading relationships...
          </Typography>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No parent/child links yet.
          </Typography>
        )}
        {!loading && parentRows.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            Parent Contracts ({parentRows.length})
          </Typography>
        )}
        {!loading &&
          parentRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" label="PARENT" color="secondary" />
                  <Chip size="small" label={row.relationship_type} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  #{row.related_contract_id} {row.related_contract_filename}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Status: {row.related_contract_status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    redirect('edit', 'contracts', row.related_contract_id)
                  }
                >
                  Open
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleDelete(row.id)}
                  disabled={deletingId === row.id}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
        {!loading && childRows.length > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            Child Contracts ({childRows.length})
          </Typography>
        )}
        {!loading &&
          childRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" label="CHILD" color="default" />
                  <Chip size="small" label={row.relationship_type} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  #{row.related_contract_id} {row.related_contract_filename}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Status: {row.related_contract_status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    redirect('edit', 'contracts', row.related_contract_id)
                  }
                >
                  Open
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleDelete(row.id)}
                  disabled={deletingId === row.id}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
      </Stack>
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          border: '1px solid #e5e7eb',
          borderRadius: 1.5,
          backgroundColor: '#f9fafb',
          width: '100%',
          maxWidth: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 1.5,
            alignItems: 'stretch',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={linkOptions}
                  loading={loadingOptions}
                  value={selectedLink}
                  onChange={(event, value) => {
                    setSelectedLink(value)
                    setRelatedId(value?.id ? String(value.id) : '')
                  }}
                  onInputChange={(event, value) => {
                    setLinkQuery(value || '')
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return ''
                    const parts: string[] = []
                    if (option.supplier_name) parts.push(option.supplier_name)
                    if (option.contract_ref) parts.push(option.contract_ref)
                    if (parts.length === 0) parts.push(option.filename)
                    return `#${option.id} ${parts.join(' | ')}`
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Search Related Contract"
                      size="small"
                      placeholder="Type supplier, contract ref, or filename"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.supplier_name || option.filename}
                          {option.contract_ref && (
                            <span style={{ marginLeft: '8px', color: '#666' }}>
                              | {option.contract_ref}
                            </span>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {[
                            option.contract_type || 'Unspecified type',
                            option.effective_date,
                            option.contract_value,
                          ]
                            .filter(Boolean)
                            .join(' • ')}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MuiTextField
                  label="Or Enter ID"
                  size="small"
                  fullWidth
                  value={relatedId}
                  onChange={(event) => setRelatedId(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <MuiTextField
                  select
                  label="Current Contract Role"
                  size="small"
                  fullWidth
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <MenuItem value="child">
                    Current is child (related is parent)
                  </MenuItem>
                  <MenuItem value="parent">
                    Current is parent (related is child)
                  </MenuItem>
                </MuiTextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <MuiTextField
                  select
                  label="Relationship Type"
                  size="small"
                  fullWidth
                  value={relationshipType}
                  onChange={(event) => setRelationshipType(event.target.value)}
                >
                  {RELATIONSHIP_TYPE_CHOICES.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </MuiTextField>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'stretch', lg: 'flex-end' },
              minWidth: { xs: '100%', lg: 'auto' },
            }}
          >
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleCreate}
              sx={{
                minWidth: 190,
                height: 40,
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              {submitting ? 'Saving...' : 'Add Relationship'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const EditActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
)

const ContractEditToolbar = (props) => (
  <Toolbar
    {...props}
    sx={{
      px: 0,
      mt: 3,
      display: 'flex',
      justifyContent: 'flex-start',
      gap: 1.5,
    }}
  >
    <SaveButton />
    <DeleteButton mutationMode="pessimistic" />
  </Toolbar>
)

const ContractStatusInput = () => {
  const record = useRecordContext()
  if (!record) return null

  const editableStatuses = ['review_required', 'completed']
  const isEditable = editableStatuses.includes(record.status)

  if (!isEditable) {
    return (
      <SelectInput
        source="status"
        label="Contract Status"
        fullWidth
        disabled
        sx={{
          mb: 0,
          '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
          '& .MuiInputBase-root': { height: 56 },
        }}
        choices={[
          { id: 'uploaded', name: 'Uploaded' },
          { id: 'processing', name: 'Processing' },
          { id: 'review_required', name: 'Review Required' },
          { id: 'completed', name: 'Verified' },
          { id: 'failed', name: 'Failed' },
          { id: 'extraction_failed', name: 'Extraction Failed' },
        ]}
      />
    )
  }

  return (
    <SelectInput
      source="status"
      label="Contract Status"
      fullWidth
      sx={{
        mb: 0,
        '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
        '& .MuiInputBase-root': { height: 56 },
      }}
      choices={[
        { id: 'review_required', name: 'Review Required' },
        { id: 'completed', name: 'Verified' },
      ]}
    />
  )
}

export const ContractEdit = () => {
  const [viewerOpen, setViewerOpen] = React.useState(false)
  const [selectedEvidence, setSelectedEvidence] = React.useState({
    fieldName: '',
    evidenceText: '',
    extractedValue: '',
  })
  const [, setHideSidebar] = useStore('contracts.review.hideSidebar', false)

  const handleSelectEvidence = React.useCallback(
    ({ fieldName, evidenceText, extractedValue }) => {
      setSelectedEvidence({
        fieldName: fieldName || '',
        evidenceText: evidenceText || '',
        extractedValue: extractedValue || '',
      })
      setViewerOpen(true)
    },
    []
  )

  React.useEffect(() => {
    setHideSidebar(viewerOpen)
    return () => {
      setHideSidebar(false)
    }
  }, [setHideSidebar, viewerOpen])

  return (
    <Edit mutationMode="pessimistic" actions={<EditActions />}>
      <Box sx={{ p: { xs: 1.5, md: 3 }, width: '100%' }}>
        <ContractSummaryHeader />
        <SimpleForm
          toolbar={<ContractEditToolbar />}
          sx={{
            '& .RaSimpleForm-form': {
              backgroundColor: '#fff',
              width: '100%',
              maxWidth: 'none',
            },
            '& .RaSimpleForm-form > *': {
              width: '100%',
              maxWidth: 'none',
            },
            '& .RaSimpleFormStack-root': {
              width: '100%',
              maxWidth: 'none',
            },
            '& .MuiInputBase-root': { borderRadius: 1.5 },
          }}
        >
          <ContractStatusPolling />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                source="vendor_id"
                reference="contracts/vendors/options"
                label="Linked Vendor"
              >
                <SelectInput optionText="name" fullWidth />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                source="contract_type"
                label="Document Type"
                fullWidth
              />
            </Grid>
          </Grid>

          <ContractRelationshipsPanel />

          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} xl={viewerOpen ? 7 : 12}>
              <ContractMetadataPanel
                onSelectEvidence={handleSelectEvidence}
                activeFieldName={viewerOpen ? selectedEvidence.fieldName : ''}
              />
            </Grid>
            {viewerOpen && (
              <Grid
                item
                xs={12}
                xl={5}
                sx={{
                  minHeight: { xl: 'calc(100vh - 48px)' },
                }}
              >
                <FormDataConsumer>
                  {({ formData }) => (
                    <ContractDocumentViewer
                      contractId={formData?.id}
                      isOpen={viewerOpen}
                      evidenceText={selectedEvidence.evidenceText}
                      extractedValue={selectedEvidence.extractedValue}
                      evidenceFieldName={selectedEvidence.fieldName}
                      onClose={() => setViewerOpen(false)}
                    />
                  )}
                </FormDataConsumer>
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mt: 6,
              backgroundColor: '#f9fafb',
              p: 4,
              width: '100%',
              borderRadius: 2,
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                mb: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Administrative Info
            </Typography>
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <ContractStatusInput />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#fff',
                    border: '1px solid #e7e5e4',
                    borderRadius: 1,
                    minHeight: '56px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mb: 0.2, lineHeight: 1 }}
                  >
                    Uploaded On
                  </Typography>
                  <DateField
                    source="created_at"
                    showTime
                    sx={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SimpleForm>
      </Box>
    </Edit>
  )
}
